import { MallCode } from 'configs/malls'
import type { HostType } from 'configs/theme'
import type { FC } from 'lib/component-utils'
import { EventCategory } from './type-utils'

export type ISO8601DateTimeString = string

export type ISO8601DateString = string

export type WebinarBannerTheme = 'lime' | 'blue' | 'purple'

export type WebinarPopupItem = {
  id: number
  textBefore: string | null
  accent: string
  textAfter: string | null
}

export type StrapiImageFormat = {
  name: string
  hash: string
  ext: string
  mime: string
  path: string | null
  width: number
  height: number
  size: number
  url: string
}

export type StrapiImageAttributes = {
  name: string
  alternativeText: string | null
  caption: string | null
  width: number
  height: number
  formats: {
    thumbnail?: StrapiImageFormat
    small?: StrapiImageFormat
    medium?: StrapiImageFormat
    large?: StrapiImageFormat
  } | null
  hash: string
  ext: string
  mime: string
  size: number
  url: string
  previewUrl: string | null
  provider: string
  provider_metadata: unknown | null
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
}

export type StrapiImage = {
  data: {
    id: number
    attributes: StrapiImageAttributes
  } | null
}

export type ChartHypothesis = {
  id: string
  mall: MallCode
  marketplaceAccountId?: number | null
  productSku?: string | null
  date: ISO8601DateString
  description?: string
}

export type ChartHypothesesStorage = {
  items: ChartHypothesis[]
}

export type BannerProps = {
  onClose: () => void
  onCtaClick: () => void
  webinarBanner?: WebinarBanner
}

export type WebinarBanner = {
  campaignId: string
  titleAccent: string | null
  titleRest: string
  subtitle: string
  cta: string
  innerUrl: string
  landingUrl: string
  extensionUrl: string | null
  popupUrl: string | null
  popupTitleAccent: string | null
  popupTitleRest: string | null
  popupItems: WebinarPopupItem[]
  extensionImage: StrapiImage
  campaignEndsAt: ISO8601DateTimeString | null
  theme: WebinarBannerTheme
  imageType: string | null
}

export type BannerListItem = {
  id: string
  component: FC<BannerProps>
  weight: number
  placements: string[] | null
  enabled: boolean
  campaignEndsAt: string | null
  hosts: HostType[]
  malls: MallCode[]
  repeatAfterDays: number[] | null
  webinarBanner?: WebinarBanner
}

export type MallStat = {
  reviewsCount: number
  productsCount: number
  brandsCount: number
  merchantsCount: number
  categoriesCount: number
  lastObserved: number
}

export type Mall = {
  code: MallCode
  currency: string
  description: string | null
  notification: string | null
  stat: MallStat
}

export type Role =
  | 'ROLE_USER'
  | 'ROLE_BRAND'
  | 'ROLE_MERCHANT'
  | 'ROLE_ADMIN'
  | 'ROLE_SUPPORT'
  | 'ROLE_SUPER_ADMIN'

export type User = {
  id: number
  email: string | null
  fullName: string | null
  active: boolean
  activeUntil: ISO8601DateTimeString | null
  totalMonthlyRevenue: number | null
  activeMalls: MallCode[]
  roles: Array<Role>
  selectedMall: string
  phone: string
  twoFactorEnabled: boolean
  switched: boolean
  originalUser: User | null
  canApplyPromo: boolean
  hasPreviousPayments: boolean
  promoCode: string | null
  customColumns: {
    context: string
    column: string
  }[]
  limits: {
    maxMallApiKeysPerMall: Partial<Record<MallCode, number>>
    externalAdsSearches: number
    maxKeywords: number
    maxNotifierAccounts: number
    imageGenerations: number
  }
}

export type ProductStatus = 'deleted' | 'archived' | 'outstocked' | 'available' | 'unknown'

export type Category = {
  url: string | null
  level: number | null
  id: number
  name: string
  actual: boolean
  hasChildren: boolean | null
  pathIds: number[]
  active: boolean
  soldOrdersPercent: number | null
}

export type Status = {
  name: ProductStatus
  ts: ISO8601DateTimeString
  subStatuses: Status[]
}

export type Merchant = {
  id: number
  link: string
  name: string
  products: number | null
  createdAt: ISO8601DateTimeString
  addedAt: ISO8601DateTimeString
  changedAt: ISO8601DateTimeString
  observedAt: ISO8601DateTimeString
  rate: number | null
  inn: string | null
  ogrn: string | null
  url: string | null
}

export type Brand = {
  id: number
  name: string
}

export type Subject = {
  id: number
  name: string
  level: number
  pathIds: number[]
  soldOrdersPercent: number | null
}

export type SubjectTree = Subject & {
  subjects: SubjectTree[]
}

export type ProductTag = {
  id: number
  name: string
}

export type ProductInfo = {
  id: number
  name: string
  mall: string
  mallUrl: string | null
  brand: Brand | null
  mpn: string | null
  merchants: Merchant[]
  images: string[]
  rate: number | null
  lastObserved: ISO8601DateTimeString
  firstObserved: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
  categories: Category[]
  subjects: Subject[]
  currency: string
  statuses: Status[]
  minPrice: number | null
  maxPrice: number | null
  minMsrp: number | null
  maxMsrp: number | null
  stock: number | null
  reviews: number | null
  reviewsCaptured: number | null
  ratingsCaptured: number | null
  orders: number | null
  inventoryTypes: InventoryType[]
  variations: ProductVariation[]
  variantsCount: number | null
  retailerId: number
  cmmf: string | null
  groupedIds: number[]
  minDiscountPrice: number | null
  maxDiscountPrice: number | null
  characteristics: { name: string; value: string }[]
  tags: ProductTag[]
}

export type FavoriteList = {
  id: number
  name: string
  mall: string
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
}

export type MetricsAggregate = {
  date: ISO8601DateTimeString
  sales: number | null
  gmv: number | null
  reviewsCount: number | null
  reviewsRate: number | null
}

export type MetricsComparison = {
  current: MetricsAggregate | null
  previous: MetricsAggregate | null
}

export const MetricsChartTypes = [
  'gmv',
  'sales',
  'prices',
  'discountPrice',
  'msrp',
  'inventory',
  'rating',
  'review',
  'orders',
  'mallReviews',
  'lostProfit',
] as const

export type MetricsChartType = (typeof MetricsChartTypes)[number]

export const OfferChartTypes = [
  'gmv',
  'sales',
  'prices',
  'msrp',
  'inventory',
  'lostProfit',
] as const

export type OfferChartType = (typeof OfferChartTypes)[number]

export const WarehouseChartTypes = ['gmv', 'sales', 'inventory'] as const

export type WarehouseChartType = (typeof WarehouseChartTypes)[number]

export const VariantChartTypes = ['gmv', 'sales', 'prices', 'inventory'] as const

export type VariantChartType = (typeof VariantChartTypes)[number]

export type ChartDataPoint = {
  date: ISO8601DateTimeString
  value: number
}

export type PriceChartDataPoint = ChartDataPoint & { min: number; max: number }

export type FeedChartDataPoint = ChartDataPoint & { ad: boolean }

export type ContentChartDataPoint = {
  date: ISO8601DateTimeString
  wordsName: number
  wordsDesc: number
  images: number
  videos: number
  characteristics: number
}

export type FeedChartPositionsRequest = {
  feedsIds: number[]
  feedType: keyof typeof FeedTypes
  from?: ISO8601DateString
  to?: ISO8601DateString
}

export type FeedChartPositions = {
  feedId: number
  positions: FeedChartDataPoint[]
}

export type MonitoringFeedKey = {
  feedType: keyof typeof FeedTypes
  feedId: number
}

export type MonitoringQueries = Record<string, MonitoringFeedKey[]>

export type QueryMonitoringExportFilter = {
  from?: ISO8601DateString
  to?: ISO8601DateString
}

// Товар в сторадже мониторинга вместе с его запросами. Хранится массивом, чтобы
// сохранять порядок товаров (ключи объекта по числам всегда сортируются по
// возрастанию и порядок не переживает перезагрузку).
export type MonitoringStoredProduct = {
  productId: number
  feeds: MonitoringFeedKey[]
}

// Ключ конкретного фида в таблице мониторинга: `${productId}:${feedType}:${feedId}`.
export type MonitoringFeedPositionKey = string

export const monitoringFeedPositionKey = (
  productId: number,
  feedType: keyof typeof FeedTypes,
  feedId: number
): MonitoringFeedPositionKey => `${productId}:${feedType}:${feedId}`

export type MonitoringProductTableItem = {
  productId: number
  product: ProductListItem | null
  selectedFeeds: MonitoringFeedKey[]
  feeds: Feed[] | undefined
}

export type ContentStat = {
  titleWordsCount: number
  descriptionWordsCount: number
  imagesCount: number
  videosCount: number
  pts: number
  characteristicsCount: number
}

export const ContentChartTypes = [
  'title',
  'description',
  'images',
  'videos',
  'characteristics',
] as const

export type ContentChartType = (typeof ContentChartTypes)[number]

export type ContentStats = {
  reference: ContentStat
  product: ContentStat
}

export type FeedType = 'category' | 'store' | 'phrase' | 'brand'

export const FeedTypes = {
  1: 'category',
  2: 'store',
  3: 'phrase',
  4: 'brand',
  6: 'banner',
} as const

export const ReverseFeedTypes = {
  category: 1,
  store: 2,
  phrase: 3,
  brand: 4,
} as const

// Порядок типов фидов в мониторинге: сначала поисковые запросы, затем остальное.
// Используется и в модалке выбора, и в таблице, и при сохранении в сторадж.
export const monitoringFeedTypeOrder: (keyof typeof FeedTypes)[] = [3, 1, 2, 4, 6]

export type Feed = {
  feedType: keyof typeof FeedTypes
  feedId: number
  entityId: number | null
  productId: number
  feedName: string
  position: number
  archived: ISO8601DateTimeString
  rank: number | null
  searchVolume: number | null
  totalProducts: number | null
  competition: number | null
  feedPath: Category[]
  url: string | null
}

export type ChartData = [Date, number][]
export type FeedChartData = [Date, number, boolean][]

export type ProductVariation = {
  id: number
  title: string
  externalId: string
  price: number | null
  maxPrice: number | null
  msrp: number | null
  maxMsrp: number | null
  imageUrl: string | null
  color: string | null
  size: string | null
  stock: number | null
  deleted: ISO8601DateTimeString | null
  inventoryTypes: InventoryType[]
  warehouses: {
    id: string
    name: string
    stock: number
  }[]
  properties: {
    id: number
    name: string
    value: string
  }[]
}

export type ProductVariationAggregates = {
  variationId: number
  sales: number | null
  gmv: number | null
}

export type Country = {
  code: string
  products: number
  reviews: number
}

export type ProductReviewFilter = {
  rateFrom?: number
  rateTo?: number
  country?: string
  firstAnswerFrom?: ISO8601DateString
  firstAnswerTo?: ISO8601DateString
  createdFrom?: ISO8601DateString
  createdTo?: ISO8601DateString
  answer?: 'all' | 'with_answer' | 'without_answer'
  sort?: `review_${'created' | 'rate' | 'country'}_${'asc' | 'desc'}`
  limit?: number
  after?: number
}

export type ProductReview = {
  id: string
  productId: number
  createdAt: ISO8601DateTimeString
  rate: number | null
  text: string | null
  countryCode: string | null
  answersInfo: {
    count: number
    firstAnswer: ISO8601DateTimeString
    lastAnswer: ISO8601DateTimeString
    allAnswersTimes: ISO8601DateTimeString[]
  } | null
  answers:
    | {
        createdAt: ISO8601DateTimeString
        text: string
      }[]
    | null
}

export type ReviewsByStar = {
  star: string
  reviews: number
}

export type ReviewsByCountry = {
  title: string
  id: string
  count: number
}

export type TimelineEvent = {
  name: 'available' | 'outstocked' | 'archived' | 'deleted' | 'created' | 'added'
  ts: ISO8601DateTimeString
}

export type BrandGroup = {
  name: string
  brandType: 'own' | 'competitor' | 'other'
  mall: string
  brandsIds: number[]
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
}

export type DecryptedBrandIds = {
  name: string
  id: number
}

export type DecryptedBrandGroup = {
  name: string
  brandType: 'own' | 'competitor' | 'other'
  mall: string
  brandsIds: DecryptedBrandIds[]
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
}

export type Subscription = {
  id: number
  activeUntil: ISO8601DateTimeString | null
  malls: MallCode[]
  price: number
  nextPrice: number
  renew: boolean
  state: 'created' | 'active' | 'stopped' | 'canceled'
  period: 'month' | '6_months' | 'year'
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
  currency: string
  nextBillAmount: number
  nextBillItems: Array<{ name: string; price: number }>
  billingService: string
  cardMask: string | null
  linkedCard: boolean
  trial: boolean
}

export type Tariff = {
  code: string
  malls: MallCode[]
  name: string
  pricePerMonth: number
  pricePer6Months: number
  pricePerYear: number
}

export type SubscriptionTariffs = {
  trial: boolean
  hasPreviousPayments: boolean
  tariffs: Tariff[]
}

export type SubscriptionPeriod = 'month' | '6_months' | 'year'

export type SubscriptionRequest = {
  tariffCode: string
  period: SubscriptionPeriod
  promo?: string
}

export type SubscriptionResponse = {
  id: number
  paymentUrl: string
}

export type CustomOffer = {
  id: number
  uuid: string
  activeUntil: ISO8601DateTimeString
  period: 'month' | '3_months' | '6_months' | 'year'
  price: number
  nextPrice: number
  billingService: string
  currency: string
  malls: MallCode[]
  user: string
  extraDays: number
  used: boolean
}

export type CustomOfferPaymentResponse = {
  paymentUrl: string
}

export type ExportListItem = {
  id: number
  hidden: boolean
  mall: string
  state: 'waiting' | 'running' | 'complete' | 'failed'
  type: string
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
  totalCount: number
  exportedCount: number
}

export type ExportList = {
  page: number
  pageSize: number
  totalCount: number
  items: ExportListItem[]
}

export type DocumentListItem = {
  id: number
  name: string
  file: string
  createdAt: ISO8601DateTimeString
}

export type DocumentList = {
  page: number
  pageSize: number
  totalCount: number
  items: DocumentListItem[]
}

export type BLArticle = {
  id: number
  name: string
  tag: string
  slug: string
  text: string
  description: string
  active: boolean
  image: {
    original: string
    big: string
  }
  lang: string
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
  imageUrl: string
}

export type BLArticleList = {
  page: number
  pageSize: number
  totalCount: number
  items: BLArticle[]
}

export type MercureMessage = {
  event: string
  data: object
}

export type ExportMercureMessage = {
  event: EventCategory<'export', 'started' | 'finished' | 'error'>
  data: {
    reportId: number
  }
}

export type ProductOfferProperty = {
  id: number
  name: string
  value: string
}

export type InventoryType =
  | 'unknown'
  | 'marketplace'
  | 'merchant'
  | 'express'
  | 'dbs'
  | 'retail'
  | 'click_collect'

export type ProductOfferInfo = {
  productId: number
  merchantId: number | null
  variationId: number
  name: string
  image: string | null
  inventory: number | null
  price: number | null
  msrp: number | null
  lastObserved: ISO8601DateTimeString
  deleted: boolean
  properties: ProductOfferProperty[]
  inventoryType: InventoryType
}

export type ProductOfferMerchant = {
  merchantId: number
  minPrice: number
  maxPrice: number
  minMsrp: number
  maxMsrp: number
  inventory: number
  merchant: Merchant
  offers: ProductOfferInfo[]
}

export type ProductOfferVariation = {
  variationId: number
  variationName: string
  minPrice: number
  maxPrice: number
  minMsrp: number
  maxMsrp: number
  inventory: number
  offers: ProductOfferInfo[]
}

export type ProductOffers = {
  id: number
  merchants: ProductOfferMerchant[]
  variations: ProductOfferVariation[]
}

export type AutocompleteSuggestion = {
  id: number
  name: string
}

export type CategoryTree = Category & {
  categories: CategoryTree[]
}

export type BusinessAvailableServices =
  | 'innerAnalytics'
  | 'autoreplier'
  | 'bidder'
  | 'repricer'
  | 'supplyBooking'

export type BusinessAvailableWbApiAccesses =
  | 'content'
  | 'analytics'
  | 'pricesAndDiscounts'
  | 'marketplace'
  | 'statistics'
  | 'finance'
  | 'promotion'
  | 'questionsAndReviews'
  | 'chatWithCustomers'
  | 'supplies'
  | 'customerReturns'
  | 'documents'
  | 'users'

export type BusinessAvailableOzonApiAccesses =
  | 'Admin'
  | 'Admin read only'
  | 'Admin read-only'
  | 'Cancelation'
  | 'Cancelation read-only'
  | 'Review'
  | 'Review read only'
  | 'Question'
  | 'Question read only'
  | 'Certification'
  | 'Certification read-only'
  | 'Pricing strategy'
  | 'Pricing strategy read-only'
  | 'Actions'
  | 'Actions read-only'
  | 'Returns'
  | 'Returns read-only'
  | 'Chat'
  | 'Chat read-only'
  | 'Supply order'
  | 'Supply order ReadOnly'
  | 'Product'
  | 'Product read-only'
  | 'Posting FBS'
  | 'Posting FBS read-only'
  | 'Brand'
  | 'Rating'
  | 'Barcode'
  | 'Want discount'
  | 'Invoice'
  | 'Poligon'
  | 'Posting FBO'
  | 'Warehouse'
  | 'Description Category'
  | 'Report'
  | 'Сервис Бухгалтерия'
  | 'Performance'

export type BusinessAvailableBeruApiAccesses = { code: string; name: string }

export type BusinessAvailableApiAccesses =
  | BusinessAvailableWbApiAccesses
  | BusinessAvailableOzonApiAccesses
  | BusinessAvailableBeruApiAccesses

export type BusinessDecodeApiTokenResponse = {
  availableServices: BusinessAvailableServices[]
  wb?: {
    allowMissingBasicToken: boolean
  }
  apiTokens: (Omit<BusinessUserApiToken, 'id' | 'brokenAt' | 'createdAt'> &
    (
      | {
          marketplaceCode: 'wb'
          wb: WbApiTokenProperties
        }
      | {
          marketplaceCode: 'ozon'
          ozon: OzonApiTokenProperties
        }
      | {
          marketplaceCode: 'beru'
          beru: BeruApiTokenProperties
        }
    ))[]
}

export type WbApiTokenProperties = {
  availableApiAccesses: BusinessAvailableWbApiAccesses[]
  accessType: 'basic' | 'service' | 'test' | 'personal'
  allowedAnalyticsService: 'Sellmonitor' | string
  readOnly: boolean
}

export type OzonApiTokenProperties = {
  serviceAccountClientId: string | null
  availableApiAccesses: BusinessAvailableOzonApiAccesses[]
  type: 'ozonBase' | 'ozonPerformance'
  premiumStatus: boolean
}

export type BeruApiTokenProperties = {
  availableApiAccesses: BusinessAvailableBeruApiAccesses[]
}

export type BusinessUserApiToken = {
  id: number
  token: string
  brokenAt: ISO8601DateTimeString | null
  expiresAt: ISO8601DateTimeString | null
  createdAt: ISO8601DateTimeString | null
} & (
  | {
      marketplaceCode: 'wb'
      wb: WbApiTokenProperties
    }
  | {
      marketplaceCode: 'ozon'
      ozon: OzonApiTokenProperties
    }
  | {
      marketplaceCode: 'beru'
      beru: BeruApiTokenProperties
    }
)

export type BusinessUserApiTokenCreationList = {
  token: string
  serviceAccountClientId: string | null
}[]

export type MarketplaceAccountTaxScheme = {
  date: ISO8601DateString
  rate: number
  scheme: 'income' | 'incomeMinusExpenses'
  vat: number
  calculateMethod: 'fromCustomerPrice' | 'fromSellerPrice'
}

export type BusinessInnerAnalyticsServiceAccount = {
  id: number
  dataAvailableFrom: ISO8601DateTimeString
  lastSyncedAt: ISO8601DateTimeString | null
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
  taxRate: number | null
  taxScheme: 'income' | 'incomeMinusExpenses' | null
  taxSchemeHistory?: MarketplaceAccountTaxScheme[]
}

export type BusinessAutoreplierServiceAccount = {
  id: number
  answersCount: number
  maxMonthlyReplies: number
  answersRemaining: number
  enabled: boolean
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
  lastReplyTime: ISO8601DateTimeString | null
  lastDays: number | null
  ai: BusinessReviewsAISettings
}

export type BusinessBidderServiceAccount = {
  currency: string
  id: number
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
}

export type BusinessSupplyBookingServiceAccount = {
  id: number
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
  authorized: boolean
}

export type BusinessUserStore = {
  /** Same id as `marketplaceAccountId` in other places */
  id: number
  /** User id on BL, same as `User.id` in `Context`. */
  clientExternalId: number[]
  /** Seller id on the marketplace, same as the one users fill when adding a new API key on Ozon, but also present on WB encoded inside the key. */
  marketplaceSellerAccountId: string
  marketplaceCode: MallCode
  needsApiTokenReplacement: boolean
  skipValidation: boolean
  availableServices: BusinessAvailableServices[]
  name: string
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
  apiTokens: BusinessUserApiToken[]
  innerAnalytics: BusinessInnerAnalyticsServiceAccount | null
  autoreplier: BusinessAutoreplierServiceAccount | null
  bidder: BusinessBidderServiceAccount | null
  supplyBooking: BusinessSupplyBookingServiceAccount | null
  ozon: {
    hasEmployeeAccessOnMarketplace: boolean
    hasEmployeeAutoreplierAccess?: boolean
    roles?: string[]
  }
  wb?: {
    allowMissingBasicToken: boolean
  }
}

export type SellersWorkScheme = 'fbo' | 'fbs' | null

export const BusinessSalesStatTypes = ['price', 'percent', 'primeCost', 'count'] as const

export type BusinessSalesStatBreakdown = {
  name: string
  description: string
  price: number
}

export type BusinessSalesStatAccountBreakdown = {
  name: string
  price?: number | null
  pricePercent?: number | null
  count?: number | null
  countPercent?: number | null
  percent?: number | null
  primeCost?: number | null
  primeCostPercent?: number | null
  customerPrice?: number | null
  marketplaceBonuses?: number | null
  coinvestment?: number | null
}

export type BusinessSalesStatMallBreakdown = {
  mall: MallCode
  price?: number | null
  pricePercent?: number | null
  count?: number | null
  countPercent?: number | null
  percent?: number | null
  primeCost?: number | null
  primeCostPercent?: number | null
  customerPrice?: number | null
  marketplaceBonuses?: number | null
  coinvestment?: number | null
  breakdownByAccounts: BusinessSalesStatAccountBreakdown[]
}

export type BusinessSalesStatPriceAndCount = {
  price?: number | null
  pricePercent?: number | null
  count?: number | null
  countPercent?: number | null
  percent?: number | null
  primeCost?: number | null
  primeCostPercent?: number | null
  expensesBreakdown?: BusinessSalesStatBreakdown[]
  breakdownByMalls?: BusinessSalesStatMallBreakdown[]
  customerPrice?: number | null
  marketplaceBonuses?: number | null
  coinvestment?: number | null
}

export type BusinessSalesStatPriceAndPercent = {
  price?: number | null
  pricePercent?: number | null
  percent?: number | null
  count?: number | null
  countPercent?: number | null
  primeCost?: number | null
  primeCostPercent?: number | null
  expensesBreakdown?: BusinessSalesStatBreakdown[]
  breakdownByMalls?: BusinessSalesStatMallBreakdown[]
  customerPrice?: number | null
  marketplaceBonuses?: number | null
  coinvestment?: number | null
}

export type BusinessSalesOrderPriceStatAccountBreakdown = BusinessSalesStatAccountBreakdown

export type BusinessSalesOrderPriceStatMallBreakdown = Omit<
  BusinessSalesStatMallBreakdown,
  'breakdownByAccounts'
> & {
  breakdownByAccounts: BusinessSalesOrderPriceStatAccountBreakdown[]
}

export type BusinessSalesOrderPriceMetricsBlock = Omit<
  BusinessSalesStatPriceAndCount,
  'breakdownByMalls'
> & {
  averagePrice?: number | null
  marketplaceBonusesPercent?: number | null
  coinvestmentPercent?: number | null
  breakdownByMalls?: BusinessSalesOrderPriceStatMallBreakdown[]
}

export type BusinessSalesAdMetricsBlock = BusinessSalesStatPriceAndPercent & {
  ordersPercent?: number | null
  salesPercent?: number | null
}

export type BusinessSalesProductCounts = {
  ordered: number | null
  inStock: number | null
  withMarketplaceDiscount: number | null
  withoutMarketplaceDiscount: number | null
}

export type BusinessSalesCurrentOrPreviousStats = {
  revenueAmount: BusinessSalesStatPriceAndCount
  payedAmount: BusinessSalesOrderPriceMetricsBlock
  profitAmount: BusinessSalesStatPriceAndPercent
  netProfitAmount: BusinessSalesStatPriceAndPercent
  payoutAmount: BusinessSalesStatPriceAndPercent
  productCounts: BusinessSalesProductCounts
  orderedAmount: BusinessSalesOrderPriceMetricsBlock
  expensesAmount: BusinessSalesStatPriceAndPercent
  primeCostExpenses: BusinessSalesStatPriceAndPercent
  additionalExpensesPerOrderTotal?: BusinessSalesStatPriceAndPercent
  compensationAmount: BusinessSalesStatPriceAndPercent
  commission: BusinessSalesStatPriceAndPercent
  storage: BusinessSalesStatPriceAndPercent
  adExpenses: BusinessSalesAdMetricsBlock
  fines: BusinessSalesStatPriceAndPercent
  paidAcceptance: BusinessSalesStatPriceAndPercent
  deduction: BusinessSalesStatPriceAndPercent
  logisticToClient: BusinessSalesStatPriceAndCount
  logisticFromClient: BusinessSalesStatPriceAndCount
  tax: Tax
  additionalExpenses: BusinessSalesStatPriceAndPercent
  returnedAmount: BusinessSalesOrderPriceMetricsBlock
  otherAmount: BusinessSalesOrderPriceMetricsBlock
  stock: BusinessSalesStatPriceAndCount
  stockFull: BusinessSalesStatPriceAndCount
  stockToClient: BusinessSalesStatPriceAndCount
  stockFromClient: BusinessSalesStatPriceAndCount
  stockOnWay: BusinessSalesStatPriceAndCount
  cancelledAmount: BusinessSalesOrderPriceMetricsBlock
  additionalService: BusinessSalesStatPriceAndPercent
  marginality: BusinessSalesStatPriceAndPercent
  returnOnInvestment: BusinessSalesStatPriceAndPercent
  /** @deprecated Kept by the API for backward compatibility. */
  marketplaceDiscountAmount: BusinessSalesStatPriceAndPercent
  revenueWithMarketplaceDiscount: BusinessSalesStatPriceAndPercent
}

export type BusinessSalesStatMergedBreakdown = {
  name: string
  description?: string
  cost?: number
  price?: number
  percent?: number
  valueType?: 'price' | 'number' | 'percent'
}

export type BaseBreakdownEntry = {
  name: string
  price: number
}

export type TaxEntryForMarketplaceAccount = {
  from: ISO8601DateString
  to: ISO8601DateString
  base: number
  baseBreakdown: BaseBreakdownEntry[]
  taxScheme: 'income' | 'incomeMinusExpenses'
  taxRate: number
  taxPrice: number
}

export type SeparateTax = {
  total: number
  entries: TaxEntryForMarketplaceAccount[]
}

export type TaxForMarketplaceAccount = {
  marketplaceAccountId: number
  marketplaceAccountName: string
  total: number
  commonTax?: SeparateTax
  vat?: SeparateTax
}

export type Tax = {
  price?: number | null
  percent?: number | null
  breakdown?: TaxForMarketplaceAccount[]
  breakdownByMalls?: BusinessSalesStatMallBreakdown[]
}

export type CommonOrdersStat = {
  period: {
    from: ISO8601DateTimeString
    to: ISO8601DateTimeString
  }
  stat: {
    orders: BusinessSalesStatPriceAndCount
    soldOrders: BusinessSalesStatPriceAndCount
    returnedOrders: BusinessSalesStatPriceAndCount
    cancelledOrders: BusinessSalesStatPriceAndCount
    deliveringOrders: BusinessSalesStatPriceAndCount
  }
}

export type BusinessSalesStats = {
  current: BusinessSalesCurrentOrPreviousStats
  previous: BusinessSalesCurrentOrPreviousStats
  commonOrdersStat: CommonOrdersStat
}

export type BusinessSalesProductSort = ListSort<
  | 'name'
  | 'rating'
  | 'reviewsCount'
  | 'orderedAmountCount'
  | 'orderedAmountPrice'
  | 'payedAmountPrice'
  | 'payedAmountCount'
  | 'returnedAmountCount'
  | 'returnedAmountPrice'
  | 'revenueAmountPrice'
  | 'profitAmountPrice'
  | 'expensesAmountPrice'
  | 'logisticPrice'
  | 'commissionPrice'
  | 'compensationPrice'
  | 'adPrice'
  | 'storagePrice'
  | 'finesPrice'
  | 'tax'
  | 'additionalExpensesPrice'
  | 'paidAcceptancePrice'
  | 'deductionPrice'
  | 'otherExpensesPrice'
  | 'primeCostExpensesPrice'
  | 'additionalExpensesPerOrderTotalPrice'
  | 'primeCostPrice'
  | 'stockCount'
  | 'stockOnWayCount'
  | 'returnOnInvestmentPercent'
  | 'marginalityPercent'
  | 'soldOrdersPercent'
>

export type BusinessSalesProductListFilter = {
  from: string
  to: string
  after: number
  limit: number
  brandId: number[]
  article: string[]
  name?: string
  search?: string[]
  sort?: BusinessSalesProductSort
  revenueAbcGroup?: string
  profitAbcGroup?: string
  marketplaceAccountIds?: number[]
  sellersWorkScheme?: SellersWorkScheme
}

export type BusinessSalesProductStats = {
  price: number | null
  count: number | null
  percent: number | null
  averagePrice?: number | null
  pricePercent?: number | null
  countPercent?: number | null
  primeCost?: number | null
  marketplaceBonuses?: number | null
  marketplaceBonusesPercent?: number | null
  coinvestment?: number | null
  coinvestmentPercent?: number | null
  ordersPercent?: number | null
  salesPercent?: number | null
  expensesBreakdown?: BusinessSalesStatBreakdown[]
}

export type BusinessSalesProductRowStats = {
  orderedAmount: BusinessSalesProductStats
  revenueAmount: BusinessSalesProductStats
  payedAmount: BusinessSalesProductStats
  returnedAmount: BusinessSalesProductStats
  expensesAmount: BusinessSalesProductStats
  profitAmount: BusinessSalesProductStats
  payoutAmount: BusinessSalesProductStats
  netProfitAmount: BusinessSalesProductStats
  netSalesAmount: BusinessSalesProductStats
  completedAmount: BusinessSalesProductStats
  averageProfitPrice: number | null
  primeCostExpenses: BusinessSalesProductStats
  additionalExpensesPerOrderTotal?: BusinessSalesProductStats
  commission: BusinessSalesProductStats
  compensationAmount: BusinessSalesProductStats
  storage: BusinessSalesProductStats
  logistic: BusinessSalesProductStats
  deliveryLogistic: BusinessSalesProductStats
  returnLogistic: BusinessSalesProductStats
  deliveryUnitLogisticPrice: number | null
  returnUnitLogisticPrice: number | null
  ad: BusinessSalesProductStats
  fines: BusinessSalesProductStats
  paidAcceptance: BusinessSalesProductStats
  deduction: BusinessSalesProductStats
  otherExpenses: BusinessSalesProductStats
  tax: Tax
  additionalExpenses: BusinessSalesProductStats
  stock: BusinessSalesProductStats
  stockToClient: BusinessSalesProductStats
  stockFromClient: BusinessSalesProductStats
  stockOnWay: BusinessSalesProductStats
  supplyRecommendation: {
    count: number
    daysSalesOfInventory: number
  }
  profitAbcAnalysis: {
    group: string
    percent: number
  }
  revenueAbcAnalysis: {
    group: string
    percent: number
  }
  commonOrdersStat: CommonOrdersStat
  returnOnInvestment: BusinessSalesProductStats
  marginality: BusinessSalesProductStats
  velocity: {
    ordersPerDay: number | null
    salesPricePerDay: number | null
  }
  lostRevenueAmount: BusinessSalesProductStats
  outOfStockDays: number | null
  lostRevenueCalculationDays: number | null
  trailingAverageDailyRevenueOnInStockDays: number | null
  payedAmountFromOrders: BusinessSalesProductStats
  returnedAmountFromOrders: BusinessSalesProductStats
  cancelledAmountFromOrders: BusinessSalesProductStats
}

export type BusinessSalesProductSize = BusinessSalesProductRowStats & {
  article: string
  size: string
  barcodes: string[]
  primeCost: number | null
  additionalExpensesPerOrderPrice?: number | null
}

export type BusinessSalesProduct = BusinessSalesProductRowStats & {
  id: number
  removed: boolean
  name: string
  brand: string
  article: string
  supplierArticle: string
  subjectName: string | null
  categoryName: string | null
  boxMultiplicity: number | null
  image: string
  url: string
  rating: number
  reviewsCount: number
  barcodes: string[]
  marketplaceAccountId: number
  primeCost: { start: number; end: number } | null
  additionalExpensesPerOrderPrice?: { start: number; end: number } | null
  sizes: BusinessSalesProductSize[]
}

export type BusinessSalesTableRow = BusinessSalesProduct | BusinessSalesProductSize

export type AbcGroupSizes = { a: number; b: number; c: number; d: number; u: number }

export type BusinessSalesProductList = {
  products: BusinessSalesProduct[]
  total: number
  profitAbcGroupSizes: AbcGroupSizes
  revenueAbcGroupSizes: AbcGroupSizes
}

export type BusinessSalesChartData = {
  ts: ISO8601DateTimeString
  ordersPrice: number | null
  ordersCustomerPrice: number | null
  ordersMarketplaceBonuses: number | null
  ordersMarketplaceBonusesPercent: number | null
  ordersCoinvestment: number | null
  ordersCoinvestmentPercent: number | null
  ordersAveragePrice: number | null
  ordersPrimeCostPrice: number | null
  ordersCount: number | null
  revenuePrice: number | null
  salesPrice: number | null
  salesCustomerPrice: number | null
  salesMarketplaceBonuses: number | null
  salesMarketplaceBonusesPercent: number | null
  salesCoinvestment: number | null
  salesCoinvestmentPercent: number | null
  salesAveragePrice: number | null
  salesPrimeCostPrice: number | null
  salesCount: number | null
  returnsPrice: number | null
  returnsCustomerPrice: number | null
  returnsMarketplaceBonuses: number | null
  returnsMarketplaceBonusesPercent: number | null
  returnsCoinvestment: number | null
  returnsCoinvestmentPercent: number | null
  returnsPrimeCostPrice: number | null
  returnsCount: number | null
  cancelsPrice: number | null
  cancelsCustomerPrice: number | null
  cancelsMarketplaceBonuses: number | null
  cancelsMarketplaceBonusesPercent: number | null
  cancelsCoinvestment: number | null
  cancelsCoinvestmentPercent: number | null
  cancelsPrimeCostPrice: number | null
  cancelsCount: number | null
  buyoutPricePercent: number | null
  buyoutCountPercent: number | null
  returnsPricePercent: number | null
  returnsCountPercent: number | null
  cancelsPricePercent: number | null
  cancelsCountPercent: number | null
  primeCostExpensesPrice: number | null
  additionalExpensesPerOrderTotalPrice?: number | null
  deliveryLogisticPrice: number | null
  returnLogisticPrice: number | null
  commissionPrice: number | null
  compensationPrice: number | null
  storagePrice: number | null
  finesPrice: number | null
  paidAcceptancePrice: number | null
  deductionPrice: number | null
  adExpensesPrice: number | null
  adOrdersPercent: number | null
  adSalesPercent: number | null
  otherExpensesPrice: number | null
  expensesPrice: number | null
  profitPrice: number | null
  payoutPrice: number | null
  netProfitPrice: number | null
  marginalityPercent: number | null
  taxPrice: number | null
  additionalExpensesPrice: number | null
  stockCount: number | null
  stockPrice: number | null
  stockPrimeCostPrice: number | null
  productCounts: BusinessSalesProductCounts | null
}

export type BusinessSalesProductChartPoints = {
  ts: ISO8601DateTimeString[]
  ordersPrice: number[]
  ordersCount: number[]
  salesPrice: number[]
  salesCount: number[]
  returnsPrice: number[]
  returnsCount: number[]
  cancelsPrice: number[]
  cancelsCount: number[]
  revenuePrice: number[]
  expensesPrice: number[]
  profitPrice: number[]
  compensationPrice: number[]
  commissionPrice: number[]
  storagePrice: number[]
  finesPrice: number[]
  paidAcceptancePrice: number[]
  deductionPrice: number[]
  otherExpensesPrice: number[]
  logisticPrice: number[]
  deliveryLogisticPrice: number[]
  returnLogisticPrice: number[]
  adExpensesPrice: number[]
  taxPrice: number[]
  additionalExpensesPrice: number[]
  availableStockCount: number[]
  primeCostExpensesPrice: number[]
  additionalExpensesPerOrderTotalPrice?: number[]
  returnOnInvestmentPercent: number[]
  marginalityPercent: number[]
  soldOrdersPercent: number[]
  salesFromOrdersPrice: number[]
  salesFromOrdersCount: number[]
  returnsFromOrdersPrice: number[]
  returnsFromOrdersCount: number[]
  cancelsFromOrdersPrice: number[]
  cancelsFromOrdersCount: number[]
}

export type BusinessSalesBrand = {
  id: number
  name: string
}

export type BusinessStockStatSort =
  | 'price_asc'
  | 'price_desc'
  | 'primeCost_asc'
  | 'primeCost_desc'
  | 'stockCount_asc'
  | 'stockCount_desc'
  | 'stockPrimeCostPrice_asc'
  | 'stockPrimeCostPrice_desc'

export type BusinessStockStatMetrics = {
  stockCount: number
  stockPrice?: number | null
  stockPrimeCostPrice?: number | null
}

export type BusinessStockStatWarehouse = {
  marketplaceAccountId: number
  marketplaceCode: MallCode
  name: string
}

export type BusinessStockStatWarehouseBreakdown = BusinessStockStatMetrics & {
  warehouseName: string
}

export type BusinessStockStatSize = BusinessStockStatMetrics & {
  size: string
  primeCost?: number | null
  additionalExpensesPerOrderPrice?: number | null
  warehouses: BusinessStockStatWarehouseBreakdown[]
}

export type BusinessStockStatProduct = BusinessStockStatMetrics & {
  marketplaceAccountId: number
  mall: MallCode
  sku: string
  supplierSku?: string | null
  name?: string | null
  brand?: string | null
  image?: string | null
  url?: string | null
  price?: number | null
  primeCost?: { start: number; end: number } | null
  additionalExpensesPerOrderPrice?: { start: number; end: number } | null
  warehouses: BusinessStockStatWarehouseBreakdown[]
  sizes: BusinessStockStatSize[]
}

export type BusinessStockStatProductList = {
  total: number
  products: BusinessStockStatProduct[]
}

export type BusinessStockStatSummary = {
  total: BusinessStockStatMetrics
  warehouses: BusinessStockStatWarehouseBreakdown[]
}

export type BusinessStockStatScope = {
  date: ISO8601DateString
  malls: MallCode[]
  marketplaceAccountIds?: number[]
}

export type BusinessStockStatFilter = BusinessStockStatScope & {
  brandId?: number[]
  warehouseNames?: string[]
}

export type BusinessStockStatProductListRequest = BusinessStockStatFilter & {
  search?: string[]
  withSizes: boolean
  after: number
  limit: number
  sort?: BusinessStockStatSort
}

export type BusinessStockStatDownloadRequest = BusinessStockStatFilter & {
  withSizes: boolean
}

export type BusinessSalesData = {
  apiKeys: BusinessUserStore[]
  apiKeysLoading: boolean
  stats: BusinessSalesStats | null
  statsLoading: boolean
  statsChartData: BusinessSalesChartData[] | null
  startsChartDataLoading: boolean
  products: BusinessSalesProduct[]
  productsTotalCount: number
  profitAbcGroupSizes: AbcGroupSizes
  revenueAbcGroupSizes: AbcGroupSizes
  productsLoading: boolean
  brands: BusinessSalesBrand[]
  brandsLoading: boolean
  hasNullPrimeCosts: boolean
  primeCostsHaveBeenChanged: boolean
  sellersWorkScheme: SellersWorkScheme
}

export type BusinessSalesPeriodFilter = {
  periodType: string
  dateFrom: string
  dateTo: string
}

// Новый формат — упорядоченный массив товаров. Старый формат (объект
// productId -> feeds) поддерживаем при чтении ради обратной совместимости.
export type QueryMonitoringStorage = Partial<
  Record<MallCode, MonitoringStoredProduct[] | Record<string, Array<number | MonitoringFeedKey>>>
>

export type BusinessGeographyFilter = {
  marketplaceAccountId?: number
  brandsIds: number[]
  skus?: string[]
}

export type BusinessAnalyticsPeriod = {
  type: string
  dateFrom: () => string
  dateTo: () => string
}

export type BusinessSalesProductFilter = {
  productsAfter: number
  productsLimit: number
  brands: number[]
  articles: string[]
  name?: string
  search?: string[]
  sort?: BusinessSalesProductSort
  revenueAbcGroup?: AbcGroup
  profitAbcGroup?: AbcGroup
}

export type BusinessSalesProductChartRequest = {
  from: ISO8601DateString | null
  to: ISO8601DateString | null
  skus: {
    sku: string
    sizes: string[]
  }[]
  charts: Exclude<keyof BusinessSalesProductChartPoints, 'ts'>[]
}

export type BusinessSalesProductChartData = {
  sku: string
  size: string
  points: BusinessSalesProductChartPoints
}[]

export type BusinessSalesExpenseCreateForm = {
  amount: number
  name: string
  marketplaceAccountIds: number[]
  date: ISO8601DateString
  sku: string | null
  size: string | null
  note: string | null
}

export type BusinessSalesExpense = BusinessSalesExpenseCreateForm & {
  id: number
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
  product: {
    sku: string
    name: string
    image: string
    url: string
  }
}

export type BusinessSalesExpenseFilter = {
  marketplaceAccountIds?: number[]
  from?: ISO8601DateString
  to?: ISO8601DateString
  search?: string | null
}

export type BusinessSalesExpenseList = {
  summary: {
    totalCost: number
    totalCostByNames:
      | {
          name: string
          cost: number
        }[]
      | null
  }
  items: BusinessSalesExpense[]
}

export type BusinessSalesExpenseNameList = {
  names: string[]
}

export type BusinessOrdersData = {
  apiKeys: BusinessUserStore[]
  apiKeysLoading: boolean
  brands: BusinessSalesBrand[]
  brandsLoading: boolean
  stats: BusinessSalesStats | null
  statsLoading: boolean
  startsChartDataLoading: boolean
  statsChartData: BusinessSalesChartData[] | null
  operationsChartData: BusinessSalesChartData[] | null
}

export type BusinessGeographyData = {
  apiKeys: BusinessUserStore[]
  brands: BusinessSalesBrand[]
  brandsLoading: boolean
  macroRegions: OrdersGeographyMacroRegionsStat | null
  macroRegionsLoading: boolean
  regionsHistory: OrdersGeographyChart | null
  regionsHistoryLoading: boolean
  movement: OrdersMovementStat | null
  movementLoading: boolean
  breakdown: OrdersGeographyList | null
  breakdownLoading: boolean
}

export type BusinessOrderStatus = 'on_delivery' | 'cancelled' | 'purchased' | 'returned'

export type BusinessPostingStatus =
  | 'acceptance_in_progress'
  | 'arbitration'
  | 'awaiting_approve'
  | 'awaiting_deliver'
  | 'awaiting_packaging'
  | 'awaiting_registration'
  | 'awaiting_verification'
  | 'cancelled'
  | 'cancelled_before_processing'
  | 'cancelled_in_delivery'
  | 'cancelled_in_processing'
  | 'cancelled_from_split_pending'
  | 'client_arbitration'
  | 'delivering'
  | 'delivery'
  | 'driver_pickup'
  | 'lost'
  | 'not_accepted'
  | 'partially_delivered'
  | 'partially_returned'
  | 'pending'
  | 'pickup'
  | 'processing'
  | 'reserved'
  | 'returned'
  | 'sent_by_seller'
  | 'unknown'
  | 'unpaid'
  | 'delivered'

export type BusinessOrdersListFilter = {
  marketplaceAccountId?: number
  from?: ISO8601DateString
  to?: ISO8601DateString
  brandsIds?: number[]
  sellersWorkScheme?: SellersWorkScheme
  orderStatus?: BusinessOrderStatus
  postingStatus?: BusinessPostingStatus
  article?: string[]
  after?: number
  limit?: number
}

export type BusinessOrderProduct = {
  id: number
  name: string
  size: string | null
  brand: string
  article: string
  supplierArticle: string
  image: string
  url: string
  rating: number
  reviewsCount: number
  primeCost: number | null
  additionalExpensesPerOrderPrice?: number | null
  barcode: string[]
  orderedPrice: number
  soldPrice: number
  returnedPrice: number
  cancelledPrice: number
  orderedQuantity: number
  soldQuantity: number
  returnedQuantity: number
  cancelledQuantity: number
  primeCostExpenses: number | null
  additionalExpensesPerOrderTotalPrice?: number | null
  logisticPrice: number | null
  commissionPrice: number | null
  expensesPrice: number | null
  profitPrice: number | null
  returnOnInvestmentPercent: number | null
  marginalityPercent: number | null
}

export type BusinessOrdersListItem = {
  externalId: string
  orderStatus: BusinessOrderStatus
  postingStatus: BusinessPostingStatus
  orderedAt: ISO8601DateTimeString | null
  cancelledAt: ISO8601DateTimeString | null
  soldAt: ISO8601DateTimeString | null
  returnedAt: ISO8601DateTimeString | null
  products: BusinessOrderProduct[]
  orderedPrice: number
  soldPrice: number
  returnedPrice: number
  cancelledPrice: number
  warehouseId: number
  warehouseName: string
  regionId: number
  regionName: string
  primeCostExpenses: number | null
  additionalExpensesPerOrderTotalPrice?: number | null
  logisticPrice: number | null
  commissionPrice: number | null
  expensesPrice: number | null
  profitPrice: number | null
  returnOnInvestmentPercent: number | null
  marginalityPercent: number | null
}

export type OrdersGeographyScopeType =
  | 'byWarehouses'
  | 'byOblasts'
  | 'byDepartureMacroRegions'
  | 'byDestinationMacroRegions'

export type OrdersGeographyGroupBy = 'byScope' | 'byProduct'

export type OrdersGeographyMetrics = {
  ordersCount?: number
  ordersPrice?: number
  localOrdersPercent?: number
  scopeOrdersPercentOfTotal?: number
  soldOrdersCount?: number
  soldOrdersPrice?: number
  soldOrdersPercent?: number
  avgOrdersCountPerDay?: number
  availableStocksCount?: number
  availableStocksPrice?: number
  availableStocksPrimeCost?: number
  stocksTurnoverDays?: number
  onWayStocksCount?: number
  outOfStockDays?: number
}

export type OrdersGeographyProductScope = OrdersGeographyMetrics & {
  scopeName: string
}

export type OrdersGeographyProductSize = OrdersGeographyMetrics & {
  size: string
  barcodes?: string[]
}

export type OrdersGeographyProductSizeWithScopes = OrdersGeographyMetrics & {
  size: string
  sizeExternalId: string
  barcodes: string[]
  scopes: OrdersGeographyProductScope[]
}

export type OrdersGeographyScopeProduct = OrdersGeographyMetrics & {
  sku: string
  supplierSku?: string
  name?: string
  image?: string
  sizes: OrdersGeographyProductSize[]
}

export type OrdersGeographyProductWithScopes = OrdersGeographyMetrics & {
  sku: string
  supplierSku?: string
  name?: string
  image?: string
  sizes?: OrdersGeographyProductSizeWithScopes[]
  scopes?: OrdersGeographyProductScope[]
}

export type OrdersGeographyScope = OrdersGeographyMetrics & {
  scopeName: string
  products: OrdersGeographyScopeProduct[]
}

export type OrdersGeographyListRequest = {
  marketplaceAccountId?: number
  from: ISO8601DateString
  to: ISO8601DateString
  brandIds?: number[]
  skus?: string[]
  scopeType: OrdersGeographyScopeType
  groupBy: OrdersGeographyGroupBy
  disableNestedBreakdown?: boolean
  disableSizesBreakdown?: boolean
}

export type OrdersGeographyList = {
  scopes?: OrdersGeographyScope[]
  products?: OrdersGeographyProductWithScopes[]
}

export type OrdersGeographyChartRequest = {
  marketplaceAccountId?: number
  from: ISO8601DateString
  to: ISO8601DateString
  brandIds?: number[]
  skus?: string[]
  scopeType: OrdersGeographyScopeType
}

export type OrdersGeographyGraphScopeMetrics = {
  scopeName: string
  ordersCount?: number
  ordersPrice?: number
  soldOrdersCount?: number
  soldOrdersPrice?: number
}

export type OrdersGeographyChartPoint = {
  ts: ISO8601DateTimeString
  scopes: OrdersGeographyGraphScopeMetrics[]
}

export type OrdersGeographyChart = {
  items: OrdersGeographyChartPoint[]
}

export type OrdersMovementStatRequest = {
  marketplaceAccountId?: number
  from: ISO8601DateString
  to: ISO8601DateString
  brandIds?: number[]
  skus?: string[]
}

export type OrdersGeographyMacroRegionsStatRequest = {
  marketplaceAccountId?: number
  from: ISO8601DateString
  to: ISO8601DateString
  brandIds?: number[]
  skus?: string[]
}

export type OrdersGeographyWarehouseStat = OrdersGeographyMetrics & {
  warehouseName: string
}

export type OrdersGeographyMacroRegionsStatItem = OrdersGeographyMetrics & {
  macroRegionName: string
  warehouses: OrdersGeographyWarehouseStat[]
}

export type OrdersGeographyMacroRegionsStat = {
  destinationMacroRegions?: OrdersGeographyMacroRegionsStatItem[]
}

export type OrdersMovementMacroRegionMetrics = {
  macroRegionName: string
  ordersCount?: number
  ordersPrice?: number
}

export type OrdersMovementMetrics = {
  destinationMacroRegions: OrdersMovementMacroRegionMetrics[]
}

export type OrdersMovementWarehouse = OrdersMovementMetrics & {
  warehouseId: number
  warehouseName: string
}

export type OrdersMovementRegion = OrdersMovementMetrics & {
  macroRegionName: string
  warehouses: OrdersMovementWarehouse[]
}

export type OrdersMovementStat = {
  departureMacroRegions: OrdersMovementRegion[]
}

export type BusinessReviewsStopword = {
  id: number
  text: string
  marketplaceAccountId: number
  updatedAt: ISO8601DateTimeString | null
}

export type BusinessReviewsAISettings = {
  responseTone: BusinessReviewsRuleGenerateTextResponseTone | null
  useEmoji: boolean
  useName: boolean
  textLength: 'short' | 'medium' | 'long' | null
}

export type BusinessReviewTemplateTextPartItem = {
  type: 'template'
  text: string
  tags: string[]
}

export type BusinessReviewAITextPartItem = {
  type: 'ai'
  prompt: string
  tags: string[]
}

export type BusinessReviewTextPartItem =
  | BusinessReviewTemplateTextPartItem
  | BusinessReviewAITextPartItem

export type BusinessReviewDefaultTag = {
  title: string
  description: string
}

export type BusinessReviewCustomTag = {
  id: number
  maId: number
  title: string
  description: string
}

export type BusinessReviewTagsList = {
  defaultTags: BusinessReviewDefaultTag[]
  customReviewTags: BusinessReviewCustomTag[]
}

export type BusinessReviewsRule = {
  id: number
  title: string
  marketplaceAccountId: number
  brandNames: string[]
  productsId: string[]
  ratingRange: number[]
  delimiterType: string
  textParts: {
    greetTexts: BusinessReviewTextPartItem[] | null
    mainTexts: BusinessReviewTextPartItem[] | null
    endTexts: BusinessReviewTextPartItem[] | null
  }
  tags: string[]
  enabled: boolean
  createdAt: ISO8601DateTimeString | null
  updatedAt: ISO8601DateTimeString | null
  answersCount: number
  replyToNonEmptyReviews: boolean
  replyToEmptyReviews: boolean
  replyToMediaOnlyReviews: boolean
  moderateBeforeSend: boolean
}

export type BusinessReviewsRuleGenerateTextResponseTone =
  | 'professional'
  | 'friendly'
  | 'energetic'
  | 'refined'

export type BusinessReviewsRuleGenerateTextRequest = {
  textPart: 'greetTexts' | 'mainTexts' | 'endTexts'
  prompt?: string
  ruleTags?: string[]
  partTags?: string[]
  ratingRange?: number[]
  textLength?: number
  responseTone?: BusinessReviewsRuleGenerateTextResponseTone
}

export type BusinessReviewsRuleGenerateTextResponse = {
  text: string
}

export type BusinessReviewsReplyPreviewRequest = {
  reviewText: string
  textParts: BusinessReviewsRule['textParts']
  ratingRange: number[]
  delimiterType: string
  tags?: string[]
}

export type BusinessReviewsReplyPreviewResponse = {
  replyText: string
}

export type BusinessReviewsBrandList = {
  id: number
  list: BusinessSalesBrand[] | null
  loading: boolean
}

export type BusinessProductNoStats = {
  id: number
  name: string | null
  brand: string
  article: string
  image: string
  url: string
  rating: number
  reviewsCount: number
  sku: string
  supplierSku?: string
}

export type BusinessReviewsProductList = {
  id: number
  list: BusinessProductNoStats[] | null
  loading: boolean
}

export type BusinessReviewsData = {
  apiKeys: BusinessUserStore[]
  apiKeysLoading: boolean
  accesses: BusinessUserStore[]
  stopwords: BusinessReviewsStopword[]
  stopwordsLoading: boolean
  reviewTags: BusinessReviewTagsList
  reviewTagsLoading: boolean
  rules: BusinessReviewsRule[]
  rulesLoading: boolean
  products: BusinessReviewsProductList[]
  brands: BusinessReviewsBrandList[]
  logs: BusinessReviewsLog[]
  logsLoading: boolean
  logsTotal: number
}

export type BusinessReviewsApiKeyStat = {
  id: number
  answersCount: number | null
  loading: boolean
}

export type BusinessReviewsStopwordFilter = {
  marketplaceAccountId?: number
  text?: string
}

export type BusinessReviewsRuleFilter = {
  marketplaceAccountId?: number
  title?: string
  productId?: string
  brandName?: string
  templateId?: number
  enabled?: boolean
  tags?: string[]
}

export type BusinessReviewTagFilter = {
  marketplaceAccountId?: number
  marketplaceCode?: string
  text?: string
  sort?: 'title_asc' | 'title_desc'
}

export type BusinessReviewsProcessedRule = {
  ruleId: number
  ruleMatchStatus:
    | 'ok'
    | 'emptyReviewText'
    | 'nonEmptyReviewText'
    | 'reviewRatingOutOfBounds'
    | 'productsOrBrandsMismatch'
    | 'tagMismatch'
    | 'reviewerNameMissing'
}

export type BusinessReviewsRuleMatchData = {
  processedRules: BusinessReviewsProcessedRule[]
  replyStatus: 'ok' | 'noMatchingRules' | 'haveStopWord' | 'manualReply'
  stopword?: string
}

export type BusinessReviewsLog = {
  marketplaceAccountId: number
  reviewId: string
  reviewerName?: string
  reviewText: string
  productId: string
  reviewRating: number
  reviewCreatedAt: ISO8601DateTimeString
  ruleId: number | null
  replyText: string | null
  repliedAt: ISO8601DateTimeString | null
  publishStatus: BusinessReviewsPublishStatus
  publishStatusAt: ISO8601DateTimeString
  ruleMatchData: BusinessReviewsRuleMatchData
  tags: string[]
}

export type BusinessReviewsLogListResponse = {
  items: BusinessReviewsLog[]
  total: number
}

export type BusinessReviewsLogRequest = {
  marketplaceAccountId?: number
  productId?: string
  offset?: number
  limit?: number
  publishStatus?: BusinessReviewsPublishStatus[]
  ratingRange?: number[]
  tags?: string[]
  sort?:
    | 'reviewRate_asc'
    | 'reviewRate_desc'
    | 'reviewCreatedAt_asc'
    | 'reviewCreatedAt_desc'
    | 'repliedAt_asc'
    | 'repliedAt_desc'
}

export type BusinessReviewsLogFilter = {
  marketplaceAccountId?: number
  productId?: string
  publishStatus?: BusinessReviewsPublishStatus[]
  ratingRange?: number[]
}

export type BusinessReviewsPublishStatus =
  | 'sent'
  | 'sending'
  | 'processing'
  | 'not_matched'
  | 'moderation'
  | 'skipped'
  | 'no_access'
  | 'removed'
  | 'already_answered'

export type BusinessReviewsReplyAction = 'post' | 'edit' | 'delete' | 'skip'

export type BusinessReviewsReplyActionRequest = {
  reviewId: string
  action: BusinessReviewsReplyAction
  replyText?: string | null
}

export type BusinessReviewsReplyRegenerateRequest = {
  reviewId: string
}

export type BusinessReviewsReplyRegenerateResponse = {
  status: string
  replyText: string | null
  ruleId: number | null
}

export type BusinessReviewsDefaultSort = {
  key: string
  order: string
}

export const BusinessReviewsFormTypeValue = ['all', 'brands', 'products'] as const

export type BusinessReviewsPagination = {
  activePage: number
  pageSize: number
}

export type BidderPageData = {
  apiKeys: BusinessUserStore[]
  accountsWithAdsKey: BusinessUserStore[]
  selectedApiKey: BusinessUserStore | null
}

export type Interval = {
  start: number
  end: number
}

export type PriceInterval = {
  min: number
  max: number
}

export type PositionInterval = {
  min: number | null
  max: number | null
}

export type DayOfWeek = 'monday'

export type BidderCampaignType = 'auto' | 'catalog_and_search'

export type BidderCampaignStatus = 'ready_to_start' | 'ended' | 'running' | 'on_pause' | 'refused'

export type BidderStatus = 'disabled' | 'running' | 'waiting'

export type BidderStatusExplanation =
  | 'highestPositionHolding'
  | 'optimalPositionExploration'
  | 'optimalPositionSearch'
  | 'optimalPositionHolding'
  | 'fixedBidPriceHolding'
  | 'fixedBidPriceWhenPositionDataUnavailable'
  | 'zeroBudget'
  | 'dailyBudgetExceeded'
  | 'outsideActivitySchedule'
  | 'targetPositionUnreachableWithMaxBid'
  | 'manualStartRequired'
  | 'unsupportedCampaignType'
  | 'unsupportedPaymentType'
  | 'inappropriateCampaignStatus'
  | 'inappropriateProductsCount'
  | 'inappropriateEnabledDisplayZones'
  | 'maxBidPriceIsBelowMinAllowable'
  | 'missingMaxBidPrice'
  | 'missingTargetPositionInterval'
  | 'noActiveTimeIntervalsInActivitySchedule'
  | 'missingActiveClusters'
  | 'missingMasterClusters'
  | 'exceededMasterClustersCount'
  | 'fixedBidPriceIsBelowMinAllowable'
  | 'missingFixedBidPrice'
  | 'missingNormQueries'
  | 'unsupportedDisplayZones'
  | 'marketplaceBidderEnabled'
  | 'bidPriceIsBelowMarketplaceMinAllowable'
  | 'currencyMismatch'

export type BidStrategy = 'highestPosition' | 'optimalPosition' | 'fixedBid'

export type BidderClusterBidPrice = {
  clusterName: string
  bid: number
}

export type BidderProductBidPrice = {
  sku: string
  value: number
  byClusters?: BidderClusterBidPrice[]
}

export type BidderCampaignListItem = {
  id: number
  externalId: string
  marketplaceAccountId: number
  name: string
  type: BidderCampaignType
  status: BidderCampaignStatus
  budget: number
  dailyBudget: number
  bidPrices: BidderProductBidPrice[]
  maxBidPrice: number | null
  fixedBidPrice: number | null
  bidStrategy: BidStrategy | null
  nonTopClusterBidStrategy: NonTopClusterBidStrategy | null
  fixedCommonBidPrice: number | null
  note: string | null
  sendNotifications: boolean
  bidderEnabled: boolean
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString | null
  startedAt: ISO8601DateTimeString | null
  endedAt: ISO8601DateTimeString | null
  bidderLastWorkedAt: ISO8601DateTimeString | null
  viewsCount: number
  clicksCount: number
  cartAdditionsCount: number
  ordersCount: number
  orderedProductsCount: number
  totalOrdersPrice: number
  totalAdExpensesPrice: number
  clickThroughRate: number
  costPerClick: number
  conversionRate: number
  clicksToCartsConversionRate: number | null
  cartsToOrdersConversionRate: number | null
  adExpensesPercent: number | null
  bidderStatus: BidderStatus
  bidderStatusExplanation: BidderStatusExplanation | null
}

export type FallbackBidStrategy = 'stopCampaign' | 'setFixedBid'

export type NonTopClusterBidStrategy = 'fixed' | 'min' | 'max' | 'avg'

export type TargetPositionCluster = 'best' | 'worst'

export type BusinessRegion = {
  id: string
  name: string
  address: string
}

export type BidderSyncResponse = {
  lastSyncedAt: ISO8601DateTimeString
}

export type BidderCampaignProduct = {
  sku: string
  deliveryTimeInHours: number
  name: string | null
  image: string | null
  brandName: string | null
  merchantName: string | null
  price: number | null
  msrp: number | null
  sales: number | null
  gmv: number | null
  rating: number | null
  reviewsCount: number | null
}

export type BidderCampaignInfo = BidderCampaignListItem & {
  currency: string
  subjectName: string | null
  setName: string | null
  menuName: string | null
  targetPositionInterval: Interval | null
  fallbackBidStrategy: FallbackBidStrategy
  fixedBidPrice: number
  minBidPrice: number
  targetPositionCluster: TargetPositionCluster
  activitySchedule: boolean[][]
  region: BusinessRegion
  lastBidderStat: BidderCampaignBidderStat | null
  paymentType: 'cpm' | 'cpc' | 'cpo'
  products: BidderCampaignProduct[] | null
  lastSyncedAt: ISO8601DateTimeString | null
  keywordsLastSyncedAt: ISO8601DateTimeString | null
  statLastSyncedAt: ISO8601DateTimeString | null
  runningCampaignsWithSameProduct: {
    id: number
    externalId: string
    name: string
  }[]
}

export type BidderCampaignUpdateInfo = Partial<{
  name: string
  note: string
  bidStrategy: BidStrategy
  fallbackBidStrategy: FallbackBidStrategy
  fixedBidPrice: number
  nonTopClusterBidStrategy: NonTopClusterBidStrategy
  fixedCommonBidPrice: number
  targetPositionCluster: TargetPositionCluster
  maxBidPrice: number
  targetPositionInterval: Interval
  bidderEnabled: boolean
  activitySchedule: boolean[][]
  regionId: string | null
  productSkus: string[]
}>

export type BidderCampaignExportFilter = {
  currency: string
  marketplaceAccountIds?: number[]
  campaignIds?: number[]
  statuses?: BidderCampaignStatus[]
  clusterIds?: number[]
  from?: ISO8601DateString
  to?: ISO8601DateString
}

export type BidderCampaignStatExportFilter = Omit<
  BidderCampaignExportFilter,
  'campaignIds' | 'clusterIds'
> &
  Pick<BidderCampaignListFilter, 'search' | 'sort'>

export type BidderCampaignKeywordsExportFilter = BidderCampaignExportFilter & {
  exportWithProducts?: boolean
}

export type BidderCampaignListFilter = {
  currency?: string
  marketplaceAccountId?: number
  name?: string
  search?: string
  type?: BidderCampaignType
  status?: BidderCampaignStatus[]
  sort?: ListSort<
    | 'name'
    | 'bidPrice'
    | 'createdAt'
    | 'bidderEnabled'
    | 'totalAdExpensesPrice'
    | 'clickThroughRate'
  >
  statFrom?: ISO8601DateString
  statTo?: ISO8601DateString
}

export type AbcGroup = 'A' | 'B' | 'C' | 'D' | 'U'

export type PositionIntervalWithObservedAt = PositionInterval & {
  observedAt?: ISO8601DateTimeString | null
}

export type SinglePositionWithObservedAt = {
  value?: number | null
  observedAt?: ISO8601DateTimeString | null
}

export type BidderKeyword = {
  name: string
  searchVolume: number | null
  adExpensesPrice: number | null
  adExpensesPercent: number | null
  clickThroughRate: number | null
  clicksCount: number | null
  cartAdditionsCount: number | null
  costPerClick: number | null
  viewsCount: number | null
  avgPosition?: PositionInterval | null
  lastDayAvgPosition?: PositionIntervalWithObservedAt | null
  lastObservedPosition?: PositionIntervalWithObservedAt | null
}

export type ClusterUserSettings = {
  maxBidPrice?: number | null
  targetPositionInterval?: Interval | null
}

export type BidderProductClusterStat = {
  sku: string
  bidPrice: number | null
  userSettings?: ClusterUserSettings | null
  viewsCount?: number | null
  clicksCount?: number | null
  cartAdditionsCount?: number | null
  ordersCount?: number | null
  adExpensesPrice?: number | null
  adExpensesPercent?: number | null
  clickThroughRate?: number | null
  costPerClick?: number | null
  avgBid?: number | null
  avgPosition?: number | null
  lastDayAvgPosition?: SinglePositionWithObservedAt | null
  lastObservedPosition?: SinglePositionWithObservedAt | null
}

export type BidderCluster = {
  id: number
  externalId: string
  name: string
  searchVolume: number | null
  activeStatus: 'master' | 'autoMaster' | 'active' | 'minus' | 'unavailable'
  keywords: BidderKeyword[]
  viewsCount: number | null
  clicksCount: number | null
  cartAdditionsCount: number | null
  ordersCount: number | null
  adExpensesPrice: number | null
  clickThroughRate: number | null
  viewsToOrdersConversionRate: number | null
  clicksToOrdersConversionRate: number | null
  costPerClick: number | null
  adExpensesPercent: number | null
  avgBid: number | null
  avgPosition: PositionInterval | null
  lastDayAvgPosition: PositionIntervalWithObservedAt | null
  lastObservedPosition: PositionIntervalWithObservedAt | null
  abcGroup: AbcGroup
  abcPercent: number
  isInClustersTop: boolean
  bidPrice: PriceInterval | null
  userSettings?: ClusterUserSettings | null
  products?: BidderProductClusterStat[]
}

export type BidderCampaignKeywords = {
  currency: string
  clusters: BidderCluster[]
  nonClusterKeywords: BidderKeyword[] | null
}

export type ProductClusterSettingsForUpdate = {
  sku: string
  maxBidPrice?: number | null
  targetPositionInterval?: Interval | null
}

export type ClusterSettingsForUpdate = {
  clusterId: number
  maxBidPrice?: number | null
  targetPositionInterval?: Interval | null
  products?: ProductClusterSettingsForUpdate[]
}

export type BidderCampaignKeywordsUpdate = {
  activeClusterIds: number[]
  masterClusterIds: number[]
  clusters?: ClusterSettingsForUpdate[]
}

export type BidderClusterMetrics = {
  clusterName: string
  viewsCount: number | null
  position: number | null
  organicPosition: number | null
}

export type BidderCampaignStat = {
  ts: ISO8601DateTimeString
  minPosition: number | null
  maxPosition: number | null
  avgPosition: number | null
  minOrganicPosition: number | null
  maxOrganicPosition: number | null
  avgOrganicPosition: number | null
  minBidPrice: number | null
  maxBidPrice: number | null
  avgBidPrice: number | null
  viewsCount: number | null
  clicksCount: number | null
  cartAdditionsCount: number | null
  ordersCount: number | null
  orderedProductsCount: number | null
  totalOrdersPrice: number | null
  totalAdExpensesPrice: number | null
  clickThroughRate: number | null
  costPerClick: number | null
  conversionRate: number | null
  clicksToCartsConversionRate: number | null
  cartsToOrdersConversionRate: number | null
  adExpensesPercent: number | null
}

export type BidderCampaignStatSummary = {
  currency: string
  viewsCount: number
  clicksCount: number
  cartAdditionsCount: number
  ordersCount: number
  orderedProductsCount: number
  totalOrdersPrice: number
  totalAdExpensesPrice: number
  clickThroughRate: number | null
  costPerClick: number | null
  conversionRate: number | null
  clicksToCartsConversionRate: number | null
  cartsToOrdersConversionRate: number | null
  adExpensesPercent: number | null
  minPosition: number | null
  maxPosition: number | null
  avgPosition: number | null
  minOrganicPosition: number | null
  maxOrganicPosition: number | null
  avgOrganicPosition: number | null
  minBidPrice: number | null
  maxBidPrice: number | null
  avgBidPrice: number | null
  products: {
    sku: string
    viewsCount: number
    clicksCount: number
    cartAdditionsCount: number
    ordersCount: number
    orderedProductsCount: number
    totalOrdersPrice: number
    totalAdExpensesPrice: number
    clickThroughRate: number | null
    costPerClick: number | null
    conversionRate: number | null
    clicksToCartsConversionRate: number | null
    cartsToOrdersConversionRate: number | null
    adExpensesPercent: number | null
    minPosition: number | null
    maxPosition: number | null
    avgPosition: number | null
    minOrganicPosition: number | null
    maxOrganicPosition: number | null
    avgOrganicPosition: number | null
    minBidPrice: number | null
    maxBidPrice: number | null
    avgBidPrice: number | null
  }[]
}

export type BidderCampaignStatAggregateFilter = {
  currency: string
  marketplaceAccountIds?: number[]
  campaignIds?: number[]
  from?: ISO8601DateString
  to?: ISO8601DateString
}

export type BidderCampaignStatAggregateMetrics = {
  viewsCount: number | null
  clicksCount: number | null
  cartAdditionsCount: number | null
  ordersCount: number | null
  orderedProductsCount: number | null
  totalOrdersPrice: number | null
  totalAdExpensesPrice: number | null
  clickThroughRate: number | null
  costPerClick: number | null
  conversionRate: number | null
  clicksToCartsConversionRate: number | null
  cartsToOrdersConversionRate: number | null
  adExpensesPercent: number | null
  minPosition: number | null
  maxPosition: number | null
  avgPosition: number | null
  minCpmBidPrice: number | null
  maxCpmBidPrice: number | null
  avgCpmBidPrice: number | null
  minCpcBidPrice: number | null
  maxCpcBidPrice: number | null
  avgCpcBidPrice: number | null
  costPerOrder: number | null
  returnOnMarketingInvestmentRate: number | null
}

export type BidderCampaignStatAggregate = BidderCampaignStatAggregateMetrics & {
  ts: ISO8601DateTimeString
}

export type BidderCampaignStatAggregateSummary = BidderCampaignStatAggregateMetrics & {
  currency: string
  viewsCount: number
  clicksCount: number
  cartAdditionsCount: number
  ordersCount: number
  orderedProductsCount: number
  totalOrdersPrice: number
  totalAdExpensesPrice: number
  totalBudget: number
  totalBalance: {
    balance: number
    net: number
    promoBonuses: number
  }
}

export type BidderActualCampaign = {
  id: number
  externalId: string
  name: string
}

export type BidderCampaignBidderStat = {
  ts: ISO8601DateTimeString
  byProducts: {
    sku: string
    clusters: {
      clusterName: string
      position: number | null
      organicPosition: number | null
      isAdPosition: boolean
      bidPrice: number | null
      actualCampaign: BidderActualCampaign | null
    }[]
  }[]
  byClusters: {
    clusterName: string
    products: {
      sku: string
      position: number | null
      organicPosition: number | null
      isAdPosition: boolean
      bidPrice: number | null
      actualCampaign: BidderActualCampaign | null
    }[]
  }[]
  startTargetPosition: number | null
  endTargetPosition: number | null
  maxBidPrice: number | null
  fixedBidPrice: number
  bidStrategy: BidStrategy | null
  fallbackBidStrategy: FallbackBidStrategy
  targetPositionCluster: TargetPositionCluster
  nonTopClusterBidStrategy: NonTopClusterBidStrategy | null
  fixedCommonBidPrice: number | null
  bidderStatus: BidderStatus
  bidderStatusExplanation: BidderStatusExplanation | null
}

export type BidderCampaignSimplifiedBidderStat = {
  ts: ISO8601DateTimeString
  bidPrice: number
  minPosition: number
  maxPosition: number
  avgPosition: number
  clustersWithUnknownPosition: number
  startTargetPosition: number
  endTargetPosition: number
  maxBidPrice: number
  fixedBidPrice: number
  bidStrategy: BidStrategy | null
  fallbackBidStrategy: FallbackBidStrategy
  targetPositionCluster: TargetPositionCluster
  nonTopClusterBidStrategy: NonTopClusterBidStrategy | null
  fixedCommonBidPrice: number | null
  bidderStatus: BidderStatus
  bidderStatusExplanation: BidderStatusExplanation | null
}

export type BidderCampaignBidDistributionRequest = {
  feedId: string
  feedType: string
  regionId: string
  adType?: BidderCampaignType
}

export type BidderCampaignBidDistribution = {
  sku: string
  deliveryTimeInHours: number | null
  page: number
  position: number
  organicPosition: number | null
  isAdPosition: boolean
  bidPrice: number | null
  belongsToCampaign: boolean
  externalAdCampaignId: string | null
  name: string | null
  image: string | null
  url: string | null
  brandName: string | null
  merchantName: string | null
  price: number | null
  msrp: number | null
  sales: number | null
  gmv: number | null
  rating: number | null
  reviewsCount: number | null
  adType: BidderCampaignType | null
}

export type BidderCampaignBidPredictionValue = {
  position: number
  bidPrice: number
}

export type BidderCampaignBidPrediction = {
  sku: string
  values: BidderCampaignBidPredictionValue[]
}

export type BidderCampaignHeatmapFilter = {
  currency: string
  marketplaceAccountId?: number
  campaignId?: number
  from?: ISO8601DateString
  to?: ISO8601DateString
}

export type BidderCampaignHeatmapValue = {
  viewsCount: number
  clicksCount: number
  ordersCount: number
  viewToClickRate: number
  clickToOrderRate: number
  viewToOrderRate: number
  costPerView: number
  costPerClick: number
  costPerOrder: number
}

export type BidderCampaignHeatmap = {
  currency: string
  dataByWeekDaysAndHours: {
    values: BidderCampaignHeatmapValue[][]
    minDeviationPercent: BidderCampaignHeatmapValue
    maxDeviationPercent: BidderCampaignHeatmapValue
    avgValue: BidderCampaignHeatmapValue
  }
  dataByWeekDays: {
    values: BidderCampaignHeatmapValue[]
    minDeviationPercent: BidderCampaignHeatmapValue
    maxDeviationPercent: BidderCampaignHeatmapValue
    avgValue: BidderCampaignHeatmapValue
  }
  dataByHours: {
    values: BidderCampaignHeatmapValue[]
    minDeviationPercent: BidderCampaignHeatmapValue
    maxDeviationPercent: BidderCampaignHeatmapValue
    avgValue: BidderCampaignHeatmapValue
  }
}

export type BidderCampaignBudgetReplenishment = {
  replenishmentStatusExplanation: 'currencyMismatch' | null
  currency: string
  budget: number
  balance: {
    balance: number
    net: number | null
  }
  replenishmentEnabled: boolean
  replenishmentSource: 'balance' | 'net'
  dailyBudgetLimitEnabled: boolean
  dailyBudgetLimit: number | null
  currentDailyExpenses: number
  lastLog: {
    createdAt: ISO8601DateTimeString
    replenishmentStatus:
      | 'NoReplenishmentRequired'
      | 'ImpossibleDueToExceedingDailyLimit'
      | 'ImpossibleDueToInsufficientBalance'
      | 'BudgetReplenished'
  } | null
}

export type BidderCampaignBudgetReplenishmentUpdate = {
  replenishmentEnabled: boolean
  replenishmentSource: 'balance' | 'net'
  dailyBudgetLimitEnabled: boolean
  dailyBudgetLimit: number | null
}

export type BidderCampaignBudgetReplenishmentLog = {
  createdAt: ISO8601DateTimeString
  replenishmentSource: 'balance' | 'net'
  dailyBudgetLimitEnabled: boolean
  dailyBudgetLimit: number | null
  currentBudget: number
  currentReplenishmentAmount: number
  currentDailyExpenses: number
  replenishmentStatus:
    | 'NoReplenishmentRequired'
    | 'ImpossibleDueToInsufficientBalance'
    | 'BudgetReplenished'
    | 'DailyLimitNotExceeded'
    | 'DailyLimitExceeded'
}

export type BidderProductCurrentPosition = {
  sku: string
  position: number | null
  organicPosition: number | null
  isAdPosition: boolean
  bidPrice: number | null
}

export type SupplyPlanningPageData = {
  apiKeys: BusinessUserStore[]
  currentSettings: SupplyRecommendationSettings
  availableSettings: SupplyRecommendationAvailableSettings
  currentReport: SupplyRecommendationReport | null
}

export type SupplyRecommendationSettings = {
  marketplaceAccountId?: number
  periodType: 'interval' | 'lastDays'
  from: ISO8601DateString
  to: ISO8601DateString
  lastDaysCount: number
  supplyDate: ISO8601DateString | null
  warehouseLoadingDays: number
  supplyCorrectionPercent: number
  calcType: SupplyRecommendationType
  stockOverflowStrategy: SupplyRecommendationOverflowStrategy
  filters?: {
    warehouseIds: number[] | null
    supplyRegionIds: number[] | null
    skus: string[] | null
  }
  warehouseIds: number[] | null
  supplyRegionIds: number[] | null
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
}

export type SupplyRecommendationAvailableSettings = {
  warehouses: { id: number; name: string; supportsSupply: boolean }[]
  supplyRegions: {
    id: number
    name: string
    warehouses: { id: number; name: string }[]
    supportsSupply: boolean
  }[]
}

export type SupplyRecommendationType = 'byWarehouses' | 'byRegions'

export type SupplyRecommendationOverflowStrategy = 'ignoreOverflow' | 'redistributeOverflow'

export type SupplyRecommendationRequest = {
  marketplaceAccountId?: number
  periodType: 'interval' | 'lastDays'
  supplyDate?: ISO8601DateString | null
  from: ISO8601DateString
  to: ISO8601DateString
  lastDaysCount: number
  warehouseLoadingDays: number
  supplyCorrectionPercent: number
  calcType: SupplyRecommendationType
  stockOverflowStrategy: SupplyRecommendationOverflowStrategy
  filters?: {
    warehouseIds?: number[] | null
    supplyRegionIds?: number[] | null
    skus?: string[] | null
  }
  warehouseIds?: number[] | null
  supplyRegiodIds?: number[] | null
}

export type SupplyRecommendationRow =
  | SupplyRecommendationProduct
  | SupplyRecommendationSize
  | SupplyRecommendationWarehouse
  | SupplyRecommendationRegion

export type SupplyRecommendationCalculations = {
  availableStocksCount: number
  deliveringStocksCount: number
  returningStocksCount: number
  salesCount: number
  salesPercent: number
  returnsCount: number
  cancelsCount: number
  salesPerDay: number
  returnsPerDay: number
  cancelsPerDay: number
  returnDeliveryDuration: number | null
  turnoverDays: number
  stocksOnSupplyDate: number | null
  salesOnSupplyDate: number | null
  stocksForWarehouseLoading: number
  stocksEndAt: number | null
  needSupplyUnits: number
  boxMultiplicity: number
  needSupplyBoxes: number
  supplyPrice: number | null
  stockTransfer: {
    total: number
    items: {
      warehouseId: number | null
      warehouseName: string | null
      supplyRegionID: number | null
      supplyRegionName: string | null
      type: 'fromDisabledSupplyScope' | 'fromUnsupportedSupplyScope' | 'excessStockTransfer'
      count: number
    }[]
  }
}

export type SupplyRecommendationWarehouse = SupplyRecommendationCalculations & {
  warehouseId: number
  warehouseName: string
  supportsSupply: boolean
}

export type SupplyRecommendationRegion = SupplyRecommendationCalculations & {
  supplyRegionId: number
  supplyRegionName: string
  supplyRegions?: SupplyRecommendationRegion[]
  supportsSupply: boolean
}

export type SupplyRecommendationSize = SupplyRecommendationCalculations & {
  size: string
  barcodes: string[]
  supplyRegions: SupplyRecommendationRegion[] | null
  warehouses: SupplyRecommendationWarehouse[] | null
}

export type SupplyRecommendationProduct = SupplyRecommendationCalculations & {
  id: number | null
  name: string | null
  marketplaceAccountId: number
  brand: string | null
  sku: string
  supplierSku: string
  image: string | null
  url: string
  rating: number | null
  primeCost: number | null
  sizes: SupplyRecommendationSize[]
}

export type SupplyRecommendationLocalization = {
  ordersCount: number | null
  ordersPrice: number | null
  ordersPercentOfTotal: number | null
  localOrdersCount: number | null
  localOrdersPrice: number | null
  localOrdersPercent: number | null
  territorialDistributionCoefficient: number | null
}

export type SupplyRecommendationSummary = {
  needSupplyUnits: number
  supplyPrice: number | null
  availableProducts: number | null
  outOfStockProducts: number | null
  willBeOutOfStockProducts: number | null
  needSupplyProducts: number | null
  warehouses: {
    warehouseId: number
    warehouseName: string
    availableStocksCount: number
    ordersCount: number
    salesCount: number
    needSupplyUnits: number
  }[]
  supplyRegions: {
    supplyRegionId: number
    supplyRegionName: string
    availableStocksCount: number
    ordersCount: number
    salesCount: number
    needSupplyUnits: number
  }[]
  currentLocalization: SupplyRecommendationLocalization
  expectedLocalization: SupplyRecommendationLocalization
  stockTransfer: {
    total: number
    items: {
      warehouseId: number | null
      warehouseName: string | null
      supplyRegionID: number | null
      supplyRegionName: string | null
      type: 'fromDisabledSupplyScope' | 'fromUnsupportedSupplyScope' | 'excessStockTransfer'
      count: number
    }[]
  }
}

export type SupplyRecommendationReport = {
  summary: SupplyRecommendationSummary
  products: SupplyRecommendationProduct[]
}

export type SupplyBookingOrder = {
  id: number
  marketplaceAccountId: number
  orderId: string
  supplyId: string
  boxTypeName: string
  monopalletQuantity: number | null
  warehouseName: string
  warehouseIsActive: boolean
  totalQuantity: number
  acceptanceCoefficient: number
  price: number
  plannedDate: ISO8601DateTimeString
  status:
    | 'notScheduled'
    | 'scheduled'
    | 'allowShipping'
    | 'acceptanceInProgress'
    | 'accepted'
    | 'virtual'
    | 'shippedOnGate'
  bookingStatus: 'off' | 'scheduled' | 'completed'
  products: {
    photo: string
    barcode: string
    subjectName: string
    colorName: string
    sellerArticle: string
    brandName: string
    quantity: number
    nmId: string
    fullName: string
  }[]
  dateFrom: ISO8601DateString
  dateTo: ISO8601DateString
  maxCoefficient: number
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
}

export type SupplyBookingOrderPlanRequest = {
  dateFrom: ISO8601DateString
  dateTo: ISO8601DateString
  maxCoefficient: number
  monopalletQuantity?: number
}

export type RepricerStoreSettings = {
  marketplaceAccountId: number
  throttle: number | null
  debounce: number | null
  marketplaceCommissionOverride?: {
    type: 'delta' | 'absolute'
    deltaPercent: number | null
    absolutePercent: number | null
  } | null
}

export type RepricerLimit = {
  marketplaceAccountId: number
  marketplaceCode: MallCode
  name: string
  activeSellerProductsLimit: number
  updatedAt: ISO8601DateTimeString
  expiresAt: ISO8601DateTimeString
}

export type RepricerScenario =
  | {
      type: 'competitors'
      enabled: boolean
      priceType: RepricerPriceType
      options: {
        priceStrategy: 'min' | 'max' | 'avg'
        competitorsProducts: {
          sku: string
          name: string
          url: string
          image: string
        }[]
        competitorsRequiredStock?: number
        offset: number
        maxPrice?: number | null
      }
    }
  | {
      type: 'schedule'
      enabled: boolean
      priceType: RepricerPriceType
      options: {
        /** 7*24 array of prices corresponding to Mon–Sun 00-23 */
        schedule: (number | null)[][] | null
      }
    }
  | {
      type: 'stock'
      enabled: boolean
      priceType: RepricerPriceType
      options: {
        units: { stock: number; price: number }[] | null
      }
    }
  | {
      type: 'crossMarketplace'
      enabled: boolean
      priceType: RepricerPriceType
      options: {
        targetProduct: RepricerCrossMarketplaceTargetProduct | null
        walletDiscountLevel?: RepricerWbWalletDiscountLevel | null
        requiredStock?: number | null
        offset: number
        maxPrice?: number | null
      }
    }

export type RepricerCrossMarketplaceTargetProductKey = {
  marketplaceCode: MallCode
  marketplaceAccountId: number
  sku: string
}

export type RepricerCrossMarketplaceTargetProduct = RepricerCrossMarketplaceTargetProductKey & {
  name?: string
  url?: string
  image?: string
  notFound?: boolean
}

export type RepricerCrossMarketplaceBlockReason =
  | 'cross_marketplace_target_product_not_owned'
  | 'cross_marketplace_no_user_with_access_to_both_accounts'

export type RepricerCrossMarketplaceChangeInfo = {
  targetProduct: RepricerCrossMarketplaceTargetProduct
  walletDiscountLevel?: RepricerWbWalletDiscountLevel | null
  basePrice: number
  offsetValue: number
  offsetPercent: number
  stock: number | null
  promotionDeviation?: RepricerPromotionDeviation
  priceJumpGuard?: RepricerPriceJumpGuard
}

export type RepricerProductAnalogue = {
  sku: string
  supplierSku: string | null
  name: string
  brand: string | null
  image: string
  subjectName: string | null
  url: string
  marketplaceAccountId: number
  mall: MallCode
}

export type RepricerBeautifulPrice = {
  enabled: boolean
  beautifyStrategy: 'up' | 'down' | 'nearest'
  template: string
}

export type RepricerTargetPrice = 'seller' | 'marketplace' | 'wallet'

export type RepricerUnreachableTargetSide = 'lower' | 'higher' | 'nearest'

export type RepricerWbWalletDiscountLevel = 'unauthorized' | 'authorized' | 'club'

export type RepricerPriceType = 'value' | 'percent'

export type RepricerPromotionSelectionStrategy =
  | 'min_threshold_all'
  | 'max_threshold'
  | 'max_boost'

export type RepricerPromotionsControl =
  | {
      mode: 'participate'
      priceType: RepricerPriceType
      maxPriceDeviation: number
      selectionStrategy: RepricerPromotionSelectionStrategy
    }
  | {
      mode: 'exit_all'
      priceType?: RepricerPriceType
    }

export type RepricerPromotionDeviation = {
  promotionId: number
  promotionName: string
  expectedPromotionPrices: {
    msrp: number
    sellerPrice: number
    marketplaceDiscountPrice: number
    walletPrice: number
  }
  expectedDeviation: number
  maxAllowedDeviation: number
}

export type RepricerPriceJumpGuardStatus =
  | 'probe'
  | 'exact'
  | 'converged'
  | 'within_tolerance'
  | 'revalidation'

export type RepricerPriceJumpGuard = {
  status: RepricerPriceJumpGuardStatus
  targetPrice: Exclude<RepricerTargetPrice, 'seller'>
  targetManagedPrice: number
  estimatedSellerPrice: number
  selectedSellerPrice: number
  expectedManagedPrice?: number | null
  unreachableTargetSide?: RepricerUnreachableTargetSide
}

export type RepricerPromotionTransition = {
  promotionId: number
  promotionName: string
  primary: boolean
  thresholdSellerPrice?: number | null
}

export type RepricerConfigSchedule = {
  enabled: boolean
  /** 7*24 array of prices corresponding to Mon–Sun 00-23 */
  entries: boolean[][]
}

export type RepricerMarginalitySettings = {
  enabled: boolean
  priceType: RepricerPriceType
  marginality: number
}

export type RepricerMinMarginality = {
  enabled: boolean
  priceType: RepricerPriceType
  marginality: number
}

export type RepricerMarginalityCalculation = {
  marginality: number
  marginPrice?: number | null
  fixedCostsPrice?: number | null
  calculationSource?: 'historical' | 'tariff' | null
  primeCost: number
  additionalExpensePerSalePrice?: number | null
  acquiringPercent?: number | null
  acquiringPrice?: number | null
  calculationDetails?: RepricerCalculationDetails | null
  netSalesCount: number
  storageExpensesPerSalePrice: number
  adExpensesPerSalePrice: number
  finesPerSalePrice: number
  paidAcceptancePerSalePrice: number
  deductionPerSalePrice: number
  logisticFromClientPerSalePrice: number
  logisticToClientPerSalePrice: number
  otherExpensesPerSalePrice: number
  fixedExpensesPerSalePrice: number
  commissionPercent: number
  commissionPrice: number
  taxScheme: 'income' | 'incomeMinusExpenses' | null
  taxRatePercent: number
  taxPrice: number
  vatPercent: number
  vatPrice: number
  totalExpectedExpensesPrice: number
  sellerDiscountPrice: number
}

export type RepricerCalculationDetails = {
  history?: { historicalPeriodFrom?: ISO8601DateString | null; historicalPeriodTo?: ISO8601DateString | null } | null
  tariff?: {
    tariffStartedAt?: ISO8601DateString | null
    product?: {
      productCategory?: string | null
      productSubject?: string | null
      productVolumeLiters?: number | null
    }
    commission?: {
      tariffCommissionPercent?: number | null
      type?: 'delta' | 'absolute' | null
      deltaPercent?: number | null
      absolutePercent?: number | null
    }
    logisticsToClient?: Record<string, number | null>
    logisticsFromClient?: Record<string, number | null>
    tariffWarehouseName?: string | null
    tariffRoute?: string | null
  } | null
  taxConfig?: { date?: ISO8601DateString | null; calculateMethod?: string | null } | null
  sellersWorkScheme?: string | null
  soldOrdersPercent?: number | null
}

export type RepricerMarginalityCalculationSkipReason =
  | 'marginality_must_be_greater_than_zero'
  | 'marginality_must_be_less_than_hundred'
  | 'financial_totals_not_found'
  | 'prime_cost_not_positive'
  | 'net_sales_count_not_positive'
  | 'no_sales'
  | 'fixed_expenses_per_sale_not_positive'
  | 'impossible_target_marginality'
  | 'product_not_found'
  | 'product_category_not_found'
  | 'product_subject_not_found'
  | 'product_volume_not_positive'
  | 'commission_tariff_not_found'
  | 'logistic_tariff_not_found'
  | 'invalid_tariff'
  | 'margin_price_must_not_be_negative'
  | 'seller_discount_price_not_positive'

export type RepricerScenarioExecutionType =
  | 'competitors'
  | 'schedule'
  | 'stock'
  | 'crossMarketplace'
  | 'marginality'
export type RepricerScenarioOrderType = RepricerScenarioExecutionType | 'fixed'

export type RepricerScenarioSkipReason =
  | RepricerMarginalityCalculationSkipReason
  | 'schedule_price_not_set_for_current_time'
  | 'stock_bound_not_matched'
  | 'competitors_not_configured'
  | 'no_available_competitors'
  | 'no_competitors_with_required_stock'
  | 'required_price_below_min_price'
  | 'required_price_above_max_price'
  | 'price_jump_above_max_price'
  | 'marginality_value_not_set'
  | RepricerCrossMarketplaceBlockReason
  | 'no_available_cross_marketplace_target_product'
  | 'no_cross_marketplace_target_product_with_required_stock'

export type RepricerScenarioSkipReasonItem = {
  type: RepricerScenarioExecutionType | 'default'
  reason: RepricerScenarioSkipReason
}

export type RepricerMarginalityPreviewItem = {
  sku: string
  name: string
  image: string
  priceType?: RepricerPriceType
  calculation: RepricerMarginalityCalculation | null
  calculationSource?: 'historical' | 'tariff' | null
  skipReason?: RepricerMarginalityCalculationSkipReason | null
}

export type RepricerJsonLogic = unknown

export type RepricerMarginalityFormulaSkipReason =
  | RepricerMarginalityCalculationSkipReason
  | 'current_product_prices_not_found'
  | 'current_product_prices_invalid'

export type RepricerMarginalityFormula = {
  marketplaceAccountId: number
  sku: string
  calculationSource?: 'historical' | 'tariff' | null
  calculator: {
    language: 'json-logic'
    version: number
    expressions: {
      sellerDiscountPrice: RepricerJsonLogic | null
      marketplaceDiscountPrice: RepricerJsonLogic | null
      walletDiscountPrice: RepricerJsonLogic | null
    }
    calculationVariants: {
      priceRange: { from: number | null; to: number | null }
      calculation: RepricerMarginalityCalculation
    }[]
  } | null
  skipReason?: RepricerMarginalityFormulaSkipReason | null
}

export type RepricerMarginalityCalculateRequest = {
  marginality: number
  priceType?: RepricerPriceType
}

export type RepricerMarginalityChangeInfo = {
  marginality?: number
  priceType?: RepricerPriceType
  marginPrice?: number | null
  actualMarginPrice?: number | null
  actualMarginality?: number
  primeCost?: number
  calculationSource?: 'historical' | 'tariff' | null
  additionalExpensePerSalePrice?: number | null
  acquiringPercent?: number | null
  acquiringPrice?: number | null
  actualAcquiringPrice?: number | null
  calculationDetails?: RepricerCalculationDetails | null
  netSalesCount?: number
  storageExpensesPerSalePrice?: number
  adExpensesPerSalePrice?: number
  finesPerSalePrice?: number
  paidAcceptancePerSalePrice?: number
  deductionPerSalePrice?: number
  logisticFromClientPerSalePrice?: number
  logisticToClientPerSalePrice?: number
  otherExpensesPerSalePrice?: number
  fixedExpensesPerSalePrice?: number
  commissionPercent?: number
  commissionPrice?: number
  actualCommissionPrice?: number
  taxScheme?: 'income' | 'incomeMinusExpenses' | null
  taxRatePercent?: number
  taxPrice?: number
  actualTaxPrice?: number
  vatPercent?: number
  vatPrice?: number
  actualVATPrice?: number
  totalExpectedExpensesPrice?: number
  actualTotalExpectedExpensesPrice?: number
  sellerDiscountPrice?: number
  managedPriceBeforeConstraints?: number
  managedPrice?: number
  constraints?: ('minPrice' | 'minMarginalityPrice' | 'maxPrice' | 'beautifulPrice')[]
  minManagedPrice?: number
  maxManagedPrice?: number
  promotionDeviation?: RepricerPromotionDeviation
  priceJumpGuard?: RepricerPriceJumpGuard
}

export type RepricerConfig = {
  regionId: string | null
  minPrice: number
  defaultPrice: number
  targetPrice: RepricerTargetPrice
  unreachableTargetSide: RepricerUnreachableTargetSide
  changeFactor: 'discount' | 'msrp'
  wbWalletDiscountLevel: RepricerWbWalletDiscountLevel
  promotionsControl: RepricerPromotionsControl
  scenarios: RepricerScenario[]
  beautifulPrice?: RepricerBeautifulPrice | null
  marginalitySettings?: RepricerMarginalitySettings | null
  minMarginality?: RepricerMinMarginality | null
  schedule: RepricerConfigSchedule | null
}

export type RepricerMinMarginalityDraft = {
  priceType: RepricerPriceType
  marginality: number | null
}

export type RepricerSettingsDraft = {
  minPrice: number | null
  minMarginality: RepricerMinMarginalityDraft
  defaultPrice: number | null
  targetPrice: RepricerTargetPrice
  unreachableTargetSide: RepricerUnreachableTargetSide
  changeFactor: 'discount' | 'msrp'
  wbWalletDiscountLevel: RepricerWbWalletDiscountLevel
  promotionsControl: RepricerPromotionsControl
}

export type RepricerProduct = {
  sku: string
  name: string
  url: string
  image: string
  defaultPrice: number | null
  hasSellersPromotions: boolean
  sellerPromotions: RepricerSellerPromotion[]
  inPriceQuarantine: boolean
  notFound: boolean
  outOfStock: boolean
  hasPriceCycle: boolean
  msrpMismatch: boolean
  negativeMarketplaceDiscount: boolean
  inSaleDueToHighInventory: boolean
  lastChange: RepricerPriceObservation | null
  lastObservation?: RepricerPriceObservation | null
}

export type RepricerPriceObservation = {
  timestamp: ISO8601DateTimeString
  oldMsrp: number
  newMsrp: number
  oldSellerDiscountPrice: number
  newSellerDiscountPrice: number
  oldMarketplaceDiscountPrice: number | null
  newMarketplaceDiscountPrice: number | null
  oldWalletDiscountPrice: number | null
  newWalletDiscountPrice: number | null
  oldMarginality?: RepricerMarginalityCalculation | null
  newMarginality?: RepricerMarginalityCalculation | null
}

export type RepricerSellerPromotion = {
  id: number
  name: string
}

export type RepricerAddRequest = {
  marketplaceAccountId: number
  products: { sku: string; defaultPrice?: number | null }[]
  tags?: string[]
  config: Omit<RepricerConfig, 'defaultPrice'> & { defaultPrice?: number }
}

export type RepricerUpdateRequest = {
  marketplaceAccountId: number
  sku: string
  tags?: string[]
  config: RepricerConfig
}

export type RepricerListFilter = {
  marketplaceAccountId?: number
  limit?: number
  after?: number
  enabled?: boolean
  tags?: string[]
  search?: string
}

export type RepricerTagListFilter = {
  marketplaceAccountId?: number
  limit?: number
  after?: number
  search?: string
}

export type RepricerProductKey = {
  marketplaceAccountId: number
  sku: string
}

export type RepricerProductMainInfo = {
  sku: string
  name: string
  url: string
  image: string
  lastChange: RepricerProduct['lastChange']
  lastObservation?: RepricerProduct['lastObservation']
}

export type RepricerProductSelector =
  | { type: 'all' }
  | { type: 'filter'; filter: Omit<RepricerListFilter, 'limit' | 'after'> }
  | { type: 'products'; products: RepricerProductKey[] }

export type RepricerListItem = {
  sku: string
  marketplaceAccountId: number
  enabled: boolean
  tags: string[]
  config: RepricerConfig
  product: RepricerProductMainInfo
  hasSellersPromotions: boolean
  inPriceQuarantine: boolean
  notFound: boolean
  outOfStock: boolean
  msrpMismatch: boolean
  negativeMarketplaceDiscount: boolean
  inSaleDueToHighInventory: boolean
  minMarginalityBlockReason?: RepricerMarginalityCalculationSkipReason | null
  crossMarketplaceBlocked?: boolean | null
  crossMarketplaceBlockReason?: RepricerCrossMarketplaceBlockReason | null
  marginalityFormula?: RepricerMarginalityFormula | null
  marginalityFormulaUnavailable?: boolean
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
}

export type RepricerTagListItem = {
  tag: string
  products: RepricerListItem[]
}

export type RepricerPriceSettingValue = {
  priceType: RepricerPriceType
  price: number
}

export type RepricerNullablePriceSettingValue = {
  priceType: RepricerPriceType
  price: number | null
}

export type RepricerSettingVariant<T> = {
  value: T
  count: number
}

export type RepricerScenarioVariants = {
  type: RepricerScenario['type']
  enabled: RepricerSettingVariant<boolean | null>[]
  priceType: RepricerSettingVariant<RepricerPriceType | null>[]
  options?: {
    priceStrategy?: RepricerSettingVariant<'min' | 'max' | 'avg' | null>[]
    competitorsRequiredStock?: RepricerSettingVariant<number | null>[]
    offset?: RepricerSettingVariant<RepricerPriceSettingValue | null>[]
    maxPrice?: RepricerSettingVariant<RepricerPriceSettingValue | null>[]
    targetProduct?: RepricerSettingVariant<RepricerCrossMarketplaceTargetProduct | null>[]
    walletDiscountLevel?: RepricerSettingVariant<RepricerWbWalletDiscountLevel | null>[]
    requiredStock?: RepricerSettingVariant<number | null>[]
  }
}

export type RepricerVariants = {
  regionId: RepricerSettingVariant<string | null>[]
  minPrice: RepricerSettingVariant<number>[]
  defaultPrice: RepricerSettingVariant<number>[]
  marginalitySettings: {
    enabled: RepricerSettingVariant<boolean>[]
    priceType: RepricerSettingVariant<RepricerPriceType>[]
    marginality: RepricerSettingVariant<RepricerNullablePriceSettingValue>[]
  }
  minMarginality: {
    enabled: RepricerSettingVariant<boolean>[]
    priceType: RepricerSettingVariant<RepricerPriceType>[]
    marginality: RepricerSettingVariant<RepricerPriceSettingValue>[]
  }
  wbWalletDiscountLevel: RepricerSettingVariant<RepricerWbWalletDiscountLevel | null>[]
  targetPrice: RepricerSettingVariant<RepricerTargetPrice>[]
  unreachableTargetSide: RepricerSettingVariant<RepricerUnreachableTargetSide>[]
  changeFactor: RepricerSettingVariant<'discount' | 'msrp'>[]
  promotionsControl: {
    mode: RepricerSettingVariant<RepricerPromotionsControl['mode']>[]
    priceType: RepricerSettingVariant<RepricerPriceType | null>[]
    maxPriceDeviation: RepricerSettingVariant<RepricerPriceSettingValue>[]
    selectionStrategy: RepricerSettingVariant<RepricerPromotionSelectionStrategy | null>[]
  }
  beautifulPrice: {
    enabled: RepricerSettingVariant<boolean | null>[]
    beautifyStrategy: RepricerSettingVariant<RepricerBeautifulPrice['beautifyStrategy'] | null>[]
    template: RepricerSettingVariant<string | null>[]
  }
  scenarios: {
    items: RepricerScenarioVariants[]
    order: RepricerSettingVariant<RepricerScenarioOrderType[] | null>[]
  }
  schedule: { enabled: RepricerSettingVariant<boolean>[] }
}

export type RepricerScenarioPatch = {
  type: RepricerScenario['type']
  enabled?: boolean | null
  priceType?: RepricerPriceType | null
  options?:
    | Partial<Extract<RepricerScenario, { type: 'competitors' }>['options']>
    | Partial<Extract<RepricerScenario, { type: 'schedule' }>['options']>
    | Partial<Extract<RepricerScenario, { type: 'stock' }>['options']>
    | (Partial<
        Omit<Extract<RepricerScenario, { type: 'crossMarketplace' }>['options'], 'targetProduct'>
      > & { targetProduct?: RepricerCrossMarketplaceTargetProductKey | null })
    | null
}

export type RepricerConfigPatch = Partial<
  Omit<
    RepricerConfig,
    'scenarios' | 'marginalitySettings' | 'minMarginality' | 'promotionsControl' | 'beautifulPrice' | 'schedule'
  >
> & {
  marginalitySettings?: Partial<RepricerMarginalitySettings> | null
  minMarginality?: Partial<RepricerMinMarginality> | null
  promotionsControl?: Partial<
    Extract<RepricerPromotionsControl, { mode: 'participate' }>
  > & { mode?: RepricerPromotionsControl['mode'] | null; priceType?: RepricerPriceType | null }
  beautifulPrice?: Partial<RepricerBeautifulPrice> | null
  scenarios?: { items?: RepricerScenarioPatch[]; order?: RepricerScenarioExecutionType[] | null } | null
  schedule?: Partial<RepricerConfigSchedule> | null
}

export type RepricerBulkError = {
  error: string
  values?: Record<string, unknown>
  products?: RepricerProductKey[]
}

export type RepricerGetProductsInfoRequest = {
  skus: string[]
}

export type RepricerPromotionsRequest = {
  marketplaceAccountId: number
  skus: string[]
}

export type RepricerPromotionProductParticipation = {
  sku: string
  entryThresholdPrice: number | null
  participating: boolean
  lastObservedAt: ISO8601DateTimeString
  minBoost: number | null
  maxBoost: number | null
  currentBoost: number | null
  minElasticPrice: number | null
  maxElasticPrice: number | null
  stock: number | null
  minStock: number | null
}

export type RepricerPromotionWithProducts = {
  promotionId: number
  name: string
  startDate: ISO8601DateTimeString
  endDate: ISO8601DateTimeString
  products: RepricerPromotionProductParticipation[]
}

export type RepricerProductPromotion = {
  promotionId: number
  name: string
  entryThresholdPrice: number | null
  startDate: ISO8601DateTimeString
  endDate: ISO8601DateTimeString
  lastObservedAt: ISO8601DateTimeString
  participating: boolean
  minBoost: number | null
  maxBoost: number | null
  currentBoost: number | null
  minElasticPrice: number | null
  maxElasticPrice: number | null
  stock: number | null
  minStock: number | null
}

export type RepricerProductWithPromotions = {
  sku: string
  promotions: RepricerProductPromotion[]
}

export type RepricerProductInfo = {
  sku: string
  name: string
  url: string
  image: string
  minMsrp: number
  avgMsrp: number
  maxMsrp: number
  minPrice: number
  avgPrice: number
  maxPrice: number
  stock: number
  defaultPrice: number | null
}

export type RepricerProductSelection = Pick<
  RepricerProductInfo,
  'sku' | 'name' | 'url' | 'image' | 'defaultPrice'
>

export type RepricerChartsRequest = {
  sku: string
  from: ISO8601DateTimeString
  to: ISO8601DateTimeString
}

export type RepricerChangelogRequest = {
  sku?: string
  promotionsChangedOnly?: boolean
  from: ISO8601DateTimeString
  to: ISO8601DateTimeString
  limit?: number
  after?: number
}

export type RepricerChartData = {
  chart:
    | ({
        msrp: number
        sellerDiscountPrice: number
        sellerDiscountPercent: number
        marketplaceDiscountPrice: number
        marketplaceDiscountPercent: number
        walletDiscountPrice: number
        walletDiscountPercent: number
        beautifulPrice: RepricerBeautifulPrice | null
        stock: number
        timestamp: ISO8601DateTimeString
        priceType: RepricerPriceType
        changeFactor: 'discount' | 'msrp'
        scenarioSkipReasons?: RepricerScenarioSkipReasonItem[]
        marginality?: RepricerMarginalityCalculation | null
      } & (
        | {
            changeReason: 'competitors'
            changeInfo: {
              priceStrategy: 'min' | 'max' | 'avg'
              basePrice: number
              offsetValue: number
              offsetPercent: number
              sku: string | null
              stock: number | null
              promotionDeviation?: RepricerPromotionDeviation
              priceJumpGuard?: RepricerPriceJumpGuard
            }
          }
        | { changeReason: 'crossMarketplace'; changeInfo: RepricerCrossMarketplaceChangeInfo }
        | {
            changeReason: 'schedule'
            changeInfo: {
              priceValue: number
              basePrice: number
              pricePercent: number
              promotionDeviation?: RepricerPromotionDeviation
              priceJumpGuard?: RepricerPriceJumpGuard
            }
          }
        | {
            changeReason: 'stock'
            changeInfo: {
              stockBound: number
              priceValue: number
              basePrice: number
              pricePercent: number
              promotionDeviation?: RepricerPromotionDeviation
              priceJumpGuard?: RepricerPriceJumpGuard
            }
          }
        | {
            changeReason: 'default'
            changeInfo: {
              defaultPrice: number
              promotionDeviation?: RepricerPromotionDeviation
              priceJumpGuard?: RepricerPriceJumpGuard
            }
          }
        | { changeReason: 'marginality'; changeInfo: RepricerMarginalityChangeInfo }
        | {
            changeReason: 'min_price'
            changeInfo: {
              minPrice: number
              byMinMarginality?: boolean
              promotionDeviation?: RepricerPromotionDeviation
              priceJumpGuard?: RepricerPriceJumpGuard
            }
          }
        | { changeReason: 'promotions_deactivation'; changeInfo: null }
        | { changeReason: 'calculate_logistics'; changeInfo: null }
        | {
            changeReason: 'slow_price_update'
            changeInfo: {
              requiredPrice: number
              targetPrice: RepricerTargetPrice
              currentStep: number
            }
          }
      ))[]
    | null
  productsMap: Record<string, { sku: string; name: string; url: string; image: string }>
}

export type RepricerChangelog = {
  total: number
  changelog: {
    timestamp: ISO8601DateTimeString
    products: ({
      sku: string
      priceType: RepricerPriceType
      oldMsrp: number
      newMsrp: number
      oldSellerDiscountPrice: number
      newSellerDiscountPrice: number
      oldSellerDiscountPercent: number
      newSellerDiscountPercent: number
      oldMarketplaceDiscountPrice: number | null
      newMarketplaceDiscountPrice: number | null
      oldMarketplaceDiscountPercent: number | null
      newMarketplaceDiscountPercent: number | null
      oldWalletDiscountPrice: number | null
      newWalletDiscountPrice: number | null
      oldWalletDiscountPercent: number | null
      newWalletDiscountPercent: number | null
      targetPrice: RepricerTargetPrice
      beautifulPrice: RepricerBeautifulPrice | null
      stock: number
      changeFactor: 'discount' | 'msrp'
      scenarioSkipReasons?: RepricerScenarioSkipReasonItem[]
      oldMarginality?: RepricerMarginalityCalculation | null
      newMarginality?: RepricerMarginalityCalculation | null
      requiredPrice?: number | null
      requiredSellersPrice?: number | null
      minMarginalityPrice?: number | null
      minMarginalityInfo?: RepricerMarginalityChangeInfo | null
      enteredPromotions: RepricerPromotionTransition[]
      exitedPromotions: RepricerPromotionTransition[]
    } & (
      | {
          changeReason: 'competitors'
          changeInfo: {
            priceStrategy: 'min' | 'max' | 'avg'
            basePrice: number
            offsetValue: number
            offsetPercent: number
            sku: string | null
            stock: number | null
            promotionDeviation?: RepricerPromotionDeviation
            priceJumpGuard?: RepricerPriceJumpGuard
          }
        }
      | { changeReason: 'crossMarketplace'; changeInfo: RepricerCrossMarketplaceChangeInfo }
      | {
          changeReason: 'schedule'
          changeInfo: {
            priceValue: number
            basePrice: number
            pricePercent: number
            promotionDeviation?: RepricerPromotionDeviation
            priceJumpGuard?: RepricerPriceJumpGuard
          }
        }
      | {
          changeReason: 'stock'
          changeInfo: {
            stockBound: number
            priceValue: number
            basePrice: number
            pricePercent: number
            promotionDeviation?: RepricerPromotionDeviation
            priceJumpGuard?: RepricerPriceJumpGuard
          }
        }
      | {
          changeReason: 'default'
          changeInfo: {
            defaultPrice: number
            promotionDeviation?: RepricerPromotionDeviation
            priceJumpGuard?: RepricerPriceJumpGuard
          }
        }
      | { changeReason: 'marginality'; changeInfo: RepricerMarginalityChangeInfo }
      | {
          changeReason: 'min_price'
          changeInfo: {
            minPrice: number
            byMinMarginality?: boolean
            promotionDeviation?: RepricerPromotionDeviation
            priceJumpGuard?: RepricerPriceJumpGuard
          }
        }
      | { changeReason: 'promotions_deactivation'; changeInfo: null }
      | { changeReason: 'calculate_logistics'; changeInfo: null }
      | {
          changeReason: 'slow_price_update'
          changeInfo: {
            requiredPrice: number
            targetPrice: RepricerTargetPrice
            currentStep: number
          }
        }
    ))[]
  }[]
  productsMap: Record<string, { sku: string; name: string; url: string; image: string }>
}

export type BusinessUnitEconomicsCalculations = {
  discountAmount: number
  discountPrice: number
  maxDiscountBeforeZeroProfit: number
  commissionPercent: number
  commissionAmount: number
  promotionExpensesAmount: number
  storing: number
  expectedReverseLogisticsCost: number
  delivery: number
  processing: number
  logistics: number
  lastMile: number
  grossProfit: number
  vatAmount: number
  taxAmount: number
  profitPerGoodPercent: number
  profitPerGoodAmount: number
  profitPerGoodsAmount: number
  revenuePerShipment: number
  volume: number
}

export type BusinessUnitEconomicsCalculationsForm = {
  price: number
  fullPrice: number
  discount: number
  quantity: number
  priceOfDelivery: number
  processingOfGoods: number
  length: number
  width: number
  height: number
  conversion: number
  promotionExpensesPercent: number
  additionalExpensesPerUnit: number
  additionalExpenses: number
  taxScheme: 'revenue' | 'profit'
  taxPercent: number
  vatPercent: number
  name: string
  category: MarketplaceCommissionCategory | null
  warehouse: CoefficientsByWarehouses | null
  departureRegion: BusinessRegion | null
  destinationRegion: BusinessRegion | null
  stock: 'fbw' | 'fbs'
}

export type BusinessUnitEconomicsCategoryRef = Pick<
  MarketplaceCommissionCategory,
  'subject' | 'category' | 'fboPercent' | 'fbsPercent'
>

export type BusinessUnitEconomicsCalculationsFormStorage = Omit<
  BusinessUnitEconomicsCalculationsForm,
  'category' | 'warehouse' | 'departureRegion' | 'destinationRegion'
> & {
  category: BusinessUnitEconomicsCategoryRef | null
  warehouseName: string | null
  departureRegionName: string | null
  destinationRegionName: string | null
}

export type BusinessUnitEconomicsCalculationStorage = {
  id: string
  mall: MallCode
  createdAt: Date
  form: BusinessUnitEconomicsCalculationsFormStorage
  calculation?: BusinessUnitEconomicsCalculations & BusinessUnitEconomicsCalculationsForm
  product?: MarketplaceCommissionCategory | null
  stock?: string
  warehouse?: CoefficientsByWarehouses | null
}

export type BusinessUnitEconomicsCalculation = Omit<
  BusinessUnitEconomicsCalculationStorage,
  'form'
> & {
  form: BusinessUnitEconomicsCalculationsForm
}

export type MarketplaceCommissionPercentByPrice = {
  priceFrom?: number | null
  priceTo?: number | null
  value: number
}

export type MarketplaceCommissionCategory = {
  category: string | null
  subject: string
  clickAndCollectPercent: number
  dbsPercent: number
  expressDbsPercent: number
  fboFreshPercent: number
  fboPercentByPrices: MarketplaceCommissionPercentByPrice[] | null
  fbsPercentByPrices: MarketplaceCommissionPercentByPrice[] | null
  fboPercent: number
  fbsPercent: number
}

export type MarketplaceCommission = {
  valuesByCategories: MarketplaceCommissionCategory[]
}

export type CoefficientsByWarehouses = {
  warehouseName: string
  type: string
  marketplaceCode: MallCode
  supportedSellerWorkSchemes: string[]
  fboLogistics: {
    coefficientPercent: number | null
  }
  fbsLogistics: {
    coefficientPercent: number | null
  }
  storage: {
    coefficientPercent: number | null
    costPerUnit: number | null
    costPerExtraUnit: number | null
  }
  createdAt: ISO8601DateTimeString
}

export type TariffCoefficientCostByVolume = {
  volumeFrom?: number | null
  volumeTo: number | null
  cost: number
}

export type OzonLogisticTariffItem = {
  departureRegion: string
  destinationRegions: {
    destinationRegion: string
    volumes: TariffCoefficientCostByVolume[]
  }[]
}

export type OzonNonLocalMarkupByDestination = {
  destinationRegion: string
  nonLocalMarkupPercent: number
}

export type OzonCommonLogistics = {
  cheapProductTariffsByVolume?: TariffCoefficientCostByVolume[] | null
  normalProductTariffsByRouteAndVolume?: OzonLogisticTariffItem[] | null
  nonLocalMarkupByDestination?: OzonNonLocalMarkupByDestination[] | null
}

export type WbCommonLogistic = {
  costByVolume?: TariffCoefficientCostByVolume[] | null
  extraLiterCost?: number | null
}

export type LogisticCoefficients = {
  fbo: {
    deliveryToPickUpPointCost: number
  }
  fbs: {
    deliveryToPickUpPointCost: number
    postingProcessing: {
      dropOffPvzCost: number
      dropOffScCost: number
      dropOffScSelfAcceptanceCost: number
      dropOffScTrustBasedAcceptanceCost: number
      pickupCost: number
    }
  }
}

export type CommonCoefficients = {
  logistics?: WbCommonLogistic | OzonCommonLogistics
}

export type TariffCoefficient = {
  coefficientsByWarehouses: CoefficientsByWarehouses[] | null
  commonCoefficients: CommonCoefficients
  logisticCoefficients: LogisticCoefficients | null
}

export type PrimeCostResponse = {
  article: string
  primeCost: number
}

export type UpdatePrimeCostRequest = {
  productSku: string
  primeCost: number
  size?: string
  date?: ISO8601DateString
}

export type DeletePrimeCostRequest = {
  productSku: string
  size?: string
  date?: ISO8601DateString
}

export type PrimeCostChange = {
  date: ISO8601DateString
  cost: { start: number; end: number }
}

export type AdditionalExpensesPerOrderChange = {
  date: ISO8601DateString
  additionalExpensesPerOrderPrice: { start: number; end: number } | null
}

export type AdditionalExpensesPerOrderForDate = Omit<PrimeCostForShipment, 'primeCost' | 'sizes'> & {
  additionalExpensesPerOrderPrice: { start: number; end: number } | null
  sizes: (Omit<PrimeCostForShipmentSize, 'primeCost'> & {
    additionalExpensesPerOrderPrice: number | null
  })[]
}

export type PrimeCostForShipmentRequest = {
  date: ISO8601DateString
  name?: string
  search?: string
  sort?: 'name_asc' | 'name_desc' | 'primeCost_asc' | 'primeCost_desc'
}

export type PrimeCostForShipmentSize = {
  size: string
  barcodes: string[]
  primeCost: number
}

export type PrimeCostForShipment = {
  id: number
  article: string
  sku: string
  supplierSku: string
  name: string
  brand: string
  image: string
  url: string
  rating: number
  reviewsCount: number
  primeCost: { start: number; end: number }
  sizes: PrimeCostForShipmentSize[]
}

export type ProductNameVersion = {
  name: string
  ts: ISO8601DateTimeString
}

export type ProductDescriptionVersion = {
  version: ISO8601DateTimeString
  text: string
}

export type StartExportResponse =
  | { id: number }
  | { error: 'ExportLimitReached'; values: { interval: number; max: number } }

export type AvailableCharacteristic = {
  code: string
  count: number
  min: number | null
  max: number | null
  percentiles: Record<number, number>
}

export type CharacteristicValue = { value: string; count: number }

type RangeFilter<T extends string> = {
  [key in `${T}${'From' | 'To'}`]?: number
}

type DateRangeFilter<T extends string> = {
  [key in `${T}${'From' | 'To'}`]?: ISO8601DateString
}

export const rangeFilters = [
  'minPrice',
  'maxPrice',
  'minMsrp',
  'maxMsrp',
  'gmv',
  'sales',
  'gmvGrow',
  'salesGrow',
  'inventory',
  'review',
  'reviewGrow',
  'ratingsCaptured',
  'rate',
  'order',
  'soldOrdersPercent',
] as const
export const dateRangeFilters = [
  'created',
  'observed',
  'reviewFirst',
  'reviewLast',
  'available',
  'outstocked',
  'archived',
  'deleted',
] as const

export type ProductRangeFilterType =
  | (typeof rangeFilters)[number]
  | (typeof dateRangeFilters)[number]

type ListSort<T extends string> = `${T}_asc` | `${T}_desc`

export type ProductListFilter = {
  name?: string
  mpn?: string
  cmmf?: string

  urls?: string[]
  retailerIds?: string[]
  ids?: number[]

  periodType?: 'grow' | 'calendar'
  periodMeasure?: 'D' | 'W' | 'M' | 'Y'
  periodNumber?: string

  inventoryTypeSales?: 'marketplace' | 'merchant'
  salesCountry?: string

  statuses?: ProductStatus[]
  inventoryTypes?: InventoryType[]
  countries?: string[]
  sort?:
    | ListSort<
        | 'created'
        | 'added'
        | 'observed'
        | 'deleted'
        | 'rate'
        | 'discount'
        | `discount_${'min' | 'avg' | 'max'}`
        | `msrp_${'min' | 'avg' | 'max'}`
        | 'likes'
        | `price_${'min' | 'avg' | 'max'}`
        | 'review'
        | `review_${'first' | 'last' | 'grow'}`
        | 'mall_reviews'
        | 'rating_count'
        | 'order'
        | 'order_grow'
        | 'sales'
        | 'sales_grow'
        | 'gmv'
        | 'gmv_grow'
        | 'id'
        | 'inventory'
        | `inventory_${'sum' | 'count' | 'min' | 'max'}`
        | 'sold_orders_percent'
      >
    | 'id'
    | 'gmv'
    | 'sales'
    | 'order'
    | 'review'
    | 'likes'
    | 'rate'

  categoriesIds?: number[]
  subjectsIds?: number[]
  merchantsIds?: number[]
  brandsIds?: number[]
  tagsIds?: number[]

  characteristicGender?: 'M' | 'W'
  characteristicCountry?: string
  characteristicColor?: string
  characteristicVolumeMin?: number
  characteristicVolumeMax?: number
  characteristicQuantityMin?: number
  characteristicQuantityMax?: number
  characteristicWeightMin?: number
  characteristicWeightMax?: number
  characteristicSizeAlphaMin?: string
  characteristicSizeAlphaMax?: string
  characteristicSizeNumMin?: number
  characteristicSizeNumMax?: number

  limit?: number
  after?: number
} & RangeFilter<(typeof rangeFilters)[number]> &
  DateRangeFilter<(typeof dateRangeFilters)[number]>

export type ProductSalesCountry = {
  country: string
  sales: number
  gmv: number
}

export type ProductListFilterStorage = Partial<Record<MallCode, ProductListFilter>>

export type ProductListItemsSummary = {
  total: number
  reviews: number
  avgRate: number
  avgPrice: number
  soldOrdersPercent: number
  sales: number
  gmv: number
}

export type AggregatePriceInfo = {
  min: number | null
  avg: number | null
  max: number | null
}

export type AggregatePropertyInfo = {
  total: number | null
}

export type ProductListItemPeriod = {
  orders: number | null
  reviews: number | null
  gmv: number | null
  sales: number | null
}

export type LegacyProductInfo = {
  id: number
  categories: Category[]
  variations: ProductVariation[]
  stock: number | null
  isStockAvailable: boolean | null
  likes: number | null
  reviewsCount: number | null
  sales: AggregatePropertyInfo | null
  gmv: AggregatePropertyInfo | null
  review: AggregatePropertyInfo | null
  question: AggregatePropertyInfo | null
  reviewCaptured: AggregatePropertyInfo | null
  order: AggregatePropertyInfo | null
  lastObserved: ISO8601DateTimeString
  firstObserved: ISO8601DateTimeString
  mallUrl: string
  title: string
  externalId: string
  code: string
  mpn: string | null
  rate: number | null
  variationCount: number | null
  brand: Brand | null
  merchant: Merchant | null
  merchants: Merchant[]
  images: string[]
  createdAt: ISO8601DateTimeString
  cmmf: string | null
  mall: string
  statuses: Status[]
}

export type ProductListItem = ProductInfo & {
  reviewFirstObserved: ISO8601DateTimeString | null
  reviewLastObserved: ISO8601DateTimeString | null
  questionFirstObserved: ISO8601DateTimeString | null
  questionLastObserved: ISO8601DateTimeString | null
  period: ProductListItemPeriod | null
  content: ContentStat | null
  soldOrdersPercent: number | null
  merchantsCount: number | null
  characteristicsNormalized: {
    gender?: ('M' | 'W')[]
    country: string
    colors: string[]
    volume: number
    weight: number
    quantity: number
    sizeAlpha: string[]
    sizeNum: number[]
  }
}

export type ListingDTO<T> = {
  total: number
  items: T[]
}

export type FilterPresetType = 'product_list' | 'keyword_list'

export type FilterPresetInfo = {
  id: number
  name: string
  type: FilterPresetType
  mall: string
}

export type FilterPreset = FilterPresetInfo & {
  data: object
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
}

export type NewUser = {
  phone: string
  fullName?: string
  email?: string
  plainPassword?: string
  authCode?: string
  adCommunicationsConsentReceived?: boolean
}

export type LoginData = {
  username: string
  password?: string
}

export type FormValidationErrors = {
  error: 'FormValidationErrors'
  formErrors: { [key: string]: string[] }
}

export type AuthCodeType = 'call' | 'sms'

export type RegisterResponse =
  | { status: 'UserRegistered'; userId: number }
  | {
      status:
        | 'UserEmailExists'
        | 'UserPhoneExists'
        | 'AuthCodeMaxAttempts'
        | 'AuthCodeExpired'
        | 'AuthCodeInvalid'
    }

export type RegisterSendAuthCodeResponse =
  | { status: 'AuthCodeSent'; phone: string; type: AuthCodeType }
  | { status: 'AttemptsLimitReached'; phone: string; type: AuthCodeType; seconds: number }
  | { status: 'CaptchaFailed' }

export type LoginResponse =
  | {
      status:
        | 'AuthSuccess'
        | 'UserNotFound'
        | 'InvalidCredentials'
        | 'UserBanned'
        | 'PasswordRequired'
    }
  | { status: 'AuthNeedTwoFactor'; phone: string; type: AuthCodeType }
  | { status: 'AttemptsLimitReached'; seconds: number }

export type LoginSubmitAuthCodeResponse = {
  status:
    | 'AuthSuccess'
    | 'AuthCodeInvalid'
    | 'AuthCodeMaxAttempts'
    | 'AuthCodeExpired'
    | 'TwoFactorNotInProgress'
}

export type RecoverySendAuthCodeResponse =
  | {
      status: 'AuthCodeSent'
      phone: string
      type: AuthCodeType
    }
  | { status: 'UserNotFound'; phone: string; type: AuthCodeType }
  | { status: 'AttemptsLimitReached'; seconds: number; phone: string; type: AuthCodeType }

export type RecoveryResetPasswordResponse =
  | {
      status: 'UserPasswordReset'
      phone: string
      username: string
      password: string
    }
  | { status: 'AuthCodeInvalid' | 'AuthCodeExpired' | 'AuthCodeMaxAttempts' }

export type WebauthnAddOptions = {
  challenge: string
  rp: {
    id: string
    name: string
  }
  user: {
    id: string
    name: string
    displayName: string
  }
  pubKeyCredParams: {
    type: 'public-key'
    alg: number
  }[]
  authenticatorSelection: {
    userVerification: 'required' | 'preferred' | 'discouraged'
  }
  attestation: 'none' | 'indirect' | 'direct'
  excludeCredentials: {
    id: string
    type: 'public-key'
  }[]
  status: 'ok' | 'error'
  errorMessage: string
}

export type WebauthnAuthenticatorCreate = {
  id: string
  type: 'public-key'
  rawId: string
  response: {
    clientDataJSON: string
    attestationObject: string
  }
}

export type WebauthnAuthenticator = {
  id: string
  type: 'public-key'
}

export type UserApiKey = {
  id: number
  name: string | null
  tokenPreview: string
  createdAt: ISO8601DateTimeString
  lastUsedAt: ISO8601DateTimeString | null
}

export type CreatedUserApiKey = UserApiKey & {
  token: string
}

export type WebauthnLogInOptions = {
  challenge: string
  rpId: string
  allowCredentials: {
    id: string
    type: 'public-key'
  }[]
  userVerification: 'required' | 'preferred' | 'discouraged'
  status: 'ok' | 'error'
  errorMessage: string
}

export type WebauthnLogInRequest = {
  id: string
  type: 'public-key'
  rawId: string
  response: {
    clientDataJSON: string
    authenticatorData: string
    signature: string
    userHandle?: string
  }
}

export type PromoCode = {
  code: string
  freeDays: number | null
  discountMonth: number | null
  discount3months: number | null
  discount6months: number | null
  discountYear: number | null
  updatedAt: ISO8601DateTimeString
  createdAt: ISO8601DateTimeString
}

export type PersonContactRequest = {
  phone: string
  name: string
  email?: string
}

export type PartnerProgramContactRequest = {
  phone: string
  name: string
  notes?: string
  agreeToProcessingPersonalData: boolean
}

export type CompanyContactRequest = {
  source?: 'main' | 'repricer'
  phone: string
  email: string
  name: string
  company: string
  agreeToProcessingPersonalData: boolean
}

export type ShopAnalysisRequest = {
  phone: string
  shopLink?: string | null
}

export type NicheInterval = 'week' | 'month' | 'quarter'

export type NicheTag = 'soon_season' | 'promising'

export type NicheObservation = {
  date: number | null
  interval: NicheInterval | null
  category: string | null
  subject: string | null
  nicheTags: string[] | null
  totalMerchants: number | null
  merchantsWithOrders: number | null
  merchantsWithOrdersDiff: number | null
  monopolizationPercent: number | null
  monopolizationPercentDiff: number | null
  gmv: number | null
  gmvDiff: number | null
  avgOrderValue: number | null
  avgOrderValueDiff: number | null
  totalProducts: number | null
  productsSold: number | null
  productsSoldDiff: number | null
  productsSoldPercent: number | null
  inventoryTurnoverDays: number | null
  availability: string | null
  inventoryAvg: number | null
  buyoutPercent: number | null
  buyoutPercentDiff: number | null
  rating: number | null
}

export type NicheObservationListResponse = {
  payload: NicheObservation[]
  total: number
  error: string | null
}

type NicheNumericFilterField =
  | 'totalMerchants'
  | 'merchantsWithOrders'
  | 'merchantsWithOrdersDiff'
  | 'monopolizationPercent'
  | 'monopolizationPercentDiff'
  | 'gmv'
  | 'gmvDiff'
  | 'avgOrderValue'
  | 'avgOrderValueDiff'
  | 'totalProducts'
  | 'productsSold'
  | 'productsSoldDiff'
  | 'productsSoldPercent'
  | 'inventoryTurnoverDays'
  | 'inventoryAvg'
  | 'buyoutPercent'
  | 'buyoutPercentDiff'
  | 'rating'

type NicheObservationSortField =
  | 'date'
  | 'interval'
  | 'category'
  | 'subject'
  | 'availability'
  | NicheNumericFilterField

export type NicheListFilter = {
  interval: NicheInterval
  category?: string
  categoryLike?: string
  subject?: string
  subjectLike?: string
  nicheTags?: NicheTag
  availability?: string
  availabilityLike?: string
  sort?: ListSort<NicheObservationSortField>
  limit?: number
  after?: number
} &
  Partial<Record<NicheNumericFilterField, number>> &
  RangeFilter<NicheNumericFilterField>

export type CategoryStatInventoryType = 'merchant' | 'marketplace' | 'mixed' | null

export type CategoryListFilter = {
  from: ISO8601DateString
  to: ISO8601DateString
  limit?: number
  after?: number
  categoryId?: number | null
  brandsIds?: number[]
  merchantsIds?: number[]
  sort?: ListSort<
    | 'sales'
    | 'gmv'
    | 'review'
    | 'brands_total'
    | 'rating_avg'
    | 'inventory_avg'
    | 'merchants_total'
    | 'products_avail'
    | 'products_sales'
  >
  inventoryType?: CategoryStatInventoryType
}

export type CategoryStatValue = {
  share: number | null
  brandsTotal: number | null
  merchantsTotal: number | null
  productsTotal: number | null
  productsObserved: number | null
  productsAvailable: number | null
  productsReviews: number | null
  productsSales: number | null
  availability: number | null
  sales: number | null
  gmv: number | null
  reviewCount: number | null
  ratingDistr: number[] | null
  wSoldPrice: number | null
  specificGmv: number | null
  specificSales: number | null
  inventoryAvg: number | null
  ratingAvg: number | null
  soldProductsShare: number | null
}

export type CategoryStatChartsQuery = {
  categoryId?: number
  from: ISO8601DateString
  to: ISO8601DateString
  brandsIds?: number[]
  merchantsIds?: number[]
  metric: CategoryStatChartType
  inventoryType?: CategoryStatInventoryType
}

export type CategoryStatCharts = Array<CategoryStatValue & { date: ISO8601DateTimeString }>

export type CategoryStatChartType = keyof Omit<
  CategoryStatValue,
  | 'availability'
  | 'share'
  | 'brandsTotal'
  | 'productsTotal'
  | 'productsSales'
  | 'productsObserved'
  | 'ratingDistr'
  | 'specificGmv'
  | 'inventoryAvg'
  | 'ratingAvg'
>

export type CategoryStatWithGrowth = CategoryStatValue & { growth: CategoryStatValue }

export type CategoryStat = CategoryStatWithGrowth & {
  category: Category
}

export type CategoryList = {
  total: number
  items: CategoryStat[]
}

export type CategoryStatBrand = CategoryStatValue & {
  brand: Brand
  rank: number
  growth: CategoryStatValue
}

export type CategoryStatMerchant = CategoryStatValue & {
  merchant: Merchant
  rank: number
  growth: CategoryStatValue
}

export type SubjectListFilter = {
  from: ISO8601DateString
  to: ISO8601DateString
  limit?: number
  after?: number
  subjectId?: number | null
  brandsIds?: number[]
  merchantsIds?: number[]
  sort?: ListSort<
    | 'sales'
    | 'gmv'
    | 'review'
    | 'brands_total'
    | 'rating_avg'
    | 'inventory_avg'
    | 'merchants_total'
    | 'products_avail'
    | 'products_sales'
  >
  inventoryType?: CategoryStatInventoryType
}

export type SubjectStat = CategoryStatWithGrowth & {
  subject: Subject
}

export type SubjectList = {
  total: number
  items: SubjectStat[]
}

export type SubjectStatChartsQuery = {
  subjectId?: number
  from: ISO8601DateString
  to: ISO8601DateString
  brandsIds?: number[]
  merchantsIds?: number[]
  metric: CategoryStatChartType
  inventoryType?: CategoryStatInventoryType
}

export type SubjectStatCharts = Array<CategoryStatValue & { date: ISO8601DateTimeString }>

export type CategoryPriceRange = {
  categoryId: number
  name: string
  priceFrom: number
  priceTo: number
  productsCount: number
  brandsCount: number | null
  merchantsCount: number | null
  gmv: number
}

export type CategoryPriceSegmentEntity = 'products' | 'brands' | 'merchants'

export type CategoryPriceSegmentFilter = Pick<
  CategoryListFilter,
  'from' | 'to' | 'brandsIds' | 'merchantsIds' | 'inventoryType'
> & {
  priceFrom?: number | null
  priceTo?: number | null
  sort: ListSort<'gmv' | 'sales' | 'price'>
  limit: number
  after: number
}

export type CategoryPriceSegmentItem = {
  rank: number
  sales: number | null
  gmv: number | null
  wSoldPrice: number | null
  growth: { sales: number | null; gmv: number | null; wSoldPrice: number | null } | null
} & (
  | { productId: number; product: ProductInfo }
  | { brand: Brand | null; merchantsTotal: number | null }
  | { merchant: Merchant | null; brandsTotal: number | null }
)

export type SubjectPriceRange = {
  categoryId: number
  name: string
  priceFrom: number
  priceTo: number
  productsCount: number
  brandsCount: number | null
  merchantsCount: number | null
  gmv: number
}

export type Keyword = {
  suggest: string
  feedId: number
  phraseId: number
  rank: number
  rankDiff: number | null
  sales: number | null
  salesDiff: number | null
  gmv: number | null
  gmvDiff: number | null
  gmvSpecific: number | null
  gmvSpecificDiff: number | null
  salesSpecific: number | null
  salesSpecificDiff: number | null
  rating: number | null
  ratingDiff: number | null
  competition: number | null
  competitionDiff: number | null
  wSoldPrice: number | null
  wSoldPriceDiff: number | null
  weight: number | null
  searchVolume: number | null
  searchVolumeDiff: number | null
  totalProducts: number | null
  totalProductsDiff: number | null
  totalMerchants: number | null
  totalMerchantsDiff: number | null
  totalBrands: number | null
  totalBrandsDiff: number | null
  productsSold: number | null
  productsSoldDiff: number | null
  productsObserved: number | null
  productsObservedDiff: number | null
  reviews: number | null
  reviewsDiff: number | null
  openCard: number | null
  openCardDiff: number | null
  addToCart: number | null
  addToCartDiff: number | null
  orders: number | null
  ordersDiff: number | null
  conversionToCart: number | null
  conversionToCartDiff: number | null
  avgPriceCart: number | null
  avgPriceCartDiff: number | null
  avgFoundProducts: number | null
  avgFoundProductsDiff: number | null
  avgMerchantsView: number | null
  avgMerchantsViewDiff: number | null
  avgProductViews: number | null
  avgProductViewsDiff: number | null
  lastObserved: ISO8601DateTimeString
  categories: Category[]
  url: string | null
}

export type KeysWithDiff<T extends { [k: string]: any }> = keyof {
  [K in keyof T as `${string & K}Diff` extends keyof Keyword ? K : never]: number
}

type KeywordListSort<T extends string> = ListSort<T | `${T}_diff`>

export const keywordRangeFilters = [
  'totalProducts',
  //'productsObserved',
  //'productsSold',
  'sales',
  'gmv',
  'wSoldPrice',
  'searchVolume',
  //'competition',
  //'rank',
  'totalMerchants',
  //'totalBrands',
  'reviews',
  'rating',
  //'salesSpecific',
  //'gmvSpecific',
] as const

export type KeywordRangeFilterType = (typeof keywordRangeFilters)[number]

export type KeywordListFilter = {
  suggest?: string
  sort?: KeywordListSort<
    | 'suggest'
    | 'sales'
    | 'gmv'
    | 'search_volume'
    | 'rank'
    | 'total_products'
    | 'products_sold'
    | 'brands_total'
    | 'merchants_total'
    | 'rating'
    | 'competition'
    | 'w_sold_price'
    | 'sales_specific'
    | 'gmv_specific'
    | 'products_observed'
    | 'reviews'
    | 'open_card'
    | 'add_to_cart'
    | 'orders'
  >
  phrasesIds?: number[]
  categoriesIds?: number[]
  subjectId?: number[]
  merchantsIds?: number[]
  brandsIds?: number[]
  limit?: number
  after?: number
} & RangeFilter<KeywordRangeFilterType>

export type KeywordBrand = {
  suggest: string
  feedId: number
  brandId: number
  brandName: string
  rank: number | null
  sales: number | null
  gmv: number | null
  gmvSpecific: number | null
  salesSpecific: number | null
  rating: number | null
  wSoldPrice: number | null
  totalMerchants: number | null
  productsSold: number | null
  productsObserved: number | null
  reviews: number | null
}

export type KeywordMerchant = {
  suggest: string
  feedId: number
  merchantId: number
  merchantName: string
  rank: number | null
  sales: number | null
  gmv: number | null
  gmvSpecific: number | null
  salesSpecific: number | null
  rating: number | null
  wSoldPrice: number | null
  totalBrands: number | null
  productsSold: number | null
  productsObserved: number | null
  reviews: number | null
}

export type KeywordStatValue = {
  date: ISO8601DateTimeString
  rank: number
  sales: number | null
  gmv: number | null
  gmvSpecific: number | null
  salesSpecific: number | null
  rating: number | null
  wSoldPrice: number | null
  searchVolume: number | null
  totalProducts: number | null
  totalMerchants: number | null
  totalBrands: number | null
  productsSold: number | null
  reviews: number | null
}

export type KeywordStatChartType = keyof Omit<KeywordStatValue, 'date'>

export type UserKeyword = {
  id: number
  mall: MallCode
  text: string
  createdAt: ISO8601DateTimeString
  updatedAt: ISO8601DateTimeString
  state: 'active' | 'processing'
}

export type UserKeywordList = {
  page: number
  pageSize: number
  totalCount: number
  items: UserKeyword[]
}

export type UserKeywordStats = {
  current: number
  max: number
}

export type QuantileRequest = {
  metrics: KeysWithDiff<Keyword>[]
  quantiles: number[]
}

export type QuantileResponse = {
  metric: KeysWithDiff<Keyword>
  quantiles: number[]
}[]

export type QuantileData = Record<KeysWithDiff<Keyword>, number[]>

export type AiFeatureName = 'generate-description' | 'assistant' | 'generate-image'

export type CustomReport = {
  code: string
  title: string
  granularity: 'days' | 'months' | 'quarters'
  description: string
  fileName: string
}

export type CustomReportCategory = {
  title: string
  exports: CustomReport[]
}

export type ActionpayAction = 'confirmed_signup' | 'paid_order'

export const actionpayGoalIds: Record<ActionpayAction, number> = {
  confirmed_signup: 23092,
  paid_order: 23093,
} as const

export type ExternalAdvertisingPublicationListFilter = {
  limit?: number
  after?: number
  sort?: ListSort<'lastSeenAt' | 'createdAt'>
}

export type ExternalAdvertisingPublication = {
  network: 'instagram' | 'telegram'
  url: string
  text?: string
  createdAt: ISO8601DateTimeString | null
  lastSeenAt: ISO8601DateTimeString
  commentCount: number | null
  reactionCount: number | null
  viewCount: number | null
}

export type ExternalAdvertisingSearchListFilter = {
  limit?: number
  after?: number
  sort?: ListSort<'createdAt'>
  createdAt?: { from: ISO8601DateTimeString; to: ISO8601DateTimeString }
  state?: ('created' | 'ongoing' | 'successful' | 'failed')[]
}

export type ExternalAdvertisingSearch = {
  mall: MallCode
  productId: string
  state: 'created' | 'ongoing' | 'successful' | 'failed'
  createdAt: ISO8601DateTimeString
}

export type ExternalAdvertisingPublicationCount = {
  productId: string
  count: number
}

export type LinkedNotifierAccount = {
  id: string
  username: string
  type: 'tg' | 'max'
}

export type NotificationSettings = {
  apiTokenStatus: { enabled: boolean }
  innerAnalyticsReport: {
    enabled: boolean
    sendAfterHour: number
    types: ('daily' | 'weekly' | 'monthly' | 'yearly')[]
    forAllMarketplaceAccounts: boolean
    forSeparateMarketplaceAccounts: boolean
    marketplaceAccounts: {
      id: number
      mall: MallCode
      name: string
      enabled: boolean
    }[]
  }
}

export type AdditionalPackType = 'image_generations'

export type AdditionalPackName<
  Pack extends AdditionalPackType,
  Count extends number,
> = `${Pack}_${Count}`

export type AdditionalPack<Pack extends AdditionalPackType, Count extends number> = {
  pack: AdditionalPackName<Pack, Count>
  count: Count
  price: number
  currency: string
}

export type AdditionalPacks = {
  [key in AdditionalPackType]: AdditionalPack<key, number>[]
}

export type ProductUpdateDescriptionRequest = {
  marketplaceAccountId: number
  productExternalId: string
  description: string
}

export type ProductUpdateImagesRequest = {
  marketplaceAccountId: number
  productExternalId: string
  images: string[]
}

export type SuggestionTypeFilter = 'similar' | 'sameSeries'

export type SuggestionPosition = {
  sourceProductId: number
  suggestion: SuggestionTypeFilter
  position: number
  archived: string
}

export type SuggestionPositionsResponse = {
  productId: number
  positions: SuggestionPosition[]
}

export type PositionsDistributionChartRow = {
  productId: number
  product: ProductListItem | null
  total: number
  counts: Record<number, number>
}

export type MessengerAuthProvider = 'telegram' | 'max'

export type MessengerAuthStartResponse = {
  attemptId: string
  loginToken: string
  expiresIn: number
  pollIntervalMs: number
  csrfToken: string
}

export type MessengerAuthStatusResponse = {
  status: 'pending' | 'confirmed' | 'completed' | 'expired' | 'cancelled'
  expiresIn: number
}

export type MessengerAuthCompleteResponse = {
  status: 'AuthSuccess'
  userId: number
  isNewUser: boolean
}

export type MessengerAuthError = {
  error: string
  values?: { seconds?: number }
}
